// pages/SAPRoleMaintenance.jsx
// [SAP Role Maintenance — TAHAP 1, READ-ONLY]
// Dashboard audit role SAP via SOAP/SOAMANAGER (RFC_READ_TABLE, path sama dgn
// SAP User Maintenance):
//   • AGR_DEFINE  → daftar role (+ parent utk derived role)
//   • AGR_TEXTS   → deskripsi (SPRAS 'E')
//   • AGR_USERS   → assignment role↔user + validitas
//   • AGR_TCODES  → tcode per role
//   • AGR_AGRS    → komposisi composite role
// Snapshot disimpan server-side (sap_rm_*) → buka halaman = instan dari cache,
// tombol "Muat Role" = refresh dari SAP. Tidak ada aksi write di tahap ini.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { usePageAIContext } from '../context/AIPageContext';
import RoleAuthFileEditor from './RoleAuthFileEditor';
import SodPanel from './SodPanel';
import LicensePanel from './LicensePanel';
import FuePanel from './FuePanel';
import {
  Server, RefreshCw, Search, ShieldCheck, Users, Layers, TerminalSquare,
  AlertCircle, X, CalendarClock, GitBranch, UserX, Info, KeyRound,
  Maximize2, Minimize2, History, ChevronRight, ArrowLeftRight,
} from 'lucide-react';

// Tab yang boleh datang dari `?tab=` (QuickSearch).
const ROLE_TABS = ['roleuser', 'users', 'usertcode', 'tcodeuser', 'userobject', 'objectuser', 'roleobject', 'objectrole', 'sod', 'license', 'fue'];

// Tab pivot dikelompokkan per pasangan arah: satu tombol untuk arah aktif,
// tombol ⇄ di sebelahnya untuk membalik arah tanpa menambah jumlah tab.
const TAB_PAIRS = [
  { key: 'roles-users',   a: 'roleuser',   b: 'users' },
  { key: 'users-tcodes',  a: 'usertcode',  b: 'tcodeuser' },
  { key: 'users-objects', a: 'userobject', b: 'objectuser' },
  { key: 'roles-objects', a: 'roleobject', b: 'objectrole' },
];
const DIR_LABEL = {
  roleuser: 'Role → Users',    users: 'User → Roles',
  usertcode: 'User → TCodes',  tcodeuser: 'TCode → Users',
  userobject: 'User → Objects', objectuser: 'Object → Users',
  roleobject: 'Role → Objects', objectrole: 'Object → Roles',
};

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T00:00:00`);
  if (isNaN(d)) return null;
  return Math.floor((d - new Date()) / 86400000);
};
// Assignment dianggap "unlimited" bila to_date kosong atau 9999-12-31.
const isUnlimited = (toDate) => !toDate || toDate.startsWith('9999');

export default function SAPRoleMaintenance() {
  const {
    lang,
    t
  } = useLang();
  const id = lang !== 'en';
  const { hasAccess } = useAuth();
  const canWrite = hasAccess('sap_roles_write');   // tahap 2: assign/unassign role

  const [profiles, setProfiles]   = useState([]);
  const [connId, setConnId]       = useState('');
  const [roles, setRoles]         = useState(null);   // null = belum dimuat
  const [assigns, setAssigns]     = useState([]);     // semua assignment (utk pivot & insight)
  const [loading, setLoading]     = useState(false);
  const [banner, setBanner]       = useState(null);
  const [meta, setMeta]           = useState(null);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [usedOnly, setUsedOnly]   = useState(true);   // hanya role yang dipakai (default)

  const [searchParams] = useSearchParams();
  const [tab, setTab]         = useState(() => {
    const p = searchParams.get('tab');
    return ROLE_TABS.includes(p) ? p : 'roleuser';
  }); // 'roleuser' (Role→User) | 'users' (User→Role) | 'usertcode'
  // Hanya tab level halaman yang disambungkan. `detailTab` dan `authView` adalah
  // tab di dalam panel detail satu role — tidak bermakna tanpa role terpilih.
  useTabDeepLink('sap_roles', (p) => ROLE_TABS.includes(p), setTab);
  const [userTcodes, setUserTcodes] = useState([]);   // hasil /user-tcodes
  const [userObjects, setUserObjects] = useState(null); // list ringan [{username, objectCount}] (null = belum dimuat)
  const [objBuilt, setObjBuilt]   = useState(false);    // cache sudah dibangun?
  const [objBuiltAt, setObjBuiltAt] = useState(null);
  const [objBusy, setObjBusy]     = useState(false);    // sedang build (baca SAP)
  const [objListBusy, setObjListBusy] = useState(false);
  const [objNote, setObjNote]     = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);  // user yang objek-nya sedang dibuka
  const [userDetail, setUserDetail] = useState({});        // cache detail per user
  const [objFilter, setObjFilter]   = useState('');        // filter object di dalam user yang dibuka
  const [tcodeDesc, setTcodeDesc]   = useState({});        // kamus tcode → deskripsi
  const [objDesc, setObjDesc]       = useState({});        // kamus object → deskripsi
  // Batas baris yang dirender untuk list panjang (TCode → Users, Object → Users)
  // supaya pindah/putar arah tab tidak menunggu ribuan node DOM.
  const LIST_STEP = 150;
  const [listLimit, setListLimit] = useState(LIST_STEP);

  // ── Role ⇄ Object ──
  const [rolePivotObj, setRolePivotObj] = useState(null);   // [{agrName, objectCount}]
  const [objRolePivot, setObjRolePivot] = useState(null);   // [{object, roleCount}]
  const [roPivotBusy, setRoPivotBusy]   = useState(false);
  const [orPivotBusy, setOrPivotBusy]   = useState(false);
  const [expandedRoleObj, setExpandedRoleObj] = useState(null);
  const [expandedObjRole, setExpandedObjRole] = useState(null);
  const [roleObjDetail, setRoleObjDetail] = useState({});   // agrName → {objects, loading, error}
  const [objRoleDetail, setObjRoleDetail] = useState({});   // object  → {roles, loading, error}

  // ── Object → User (kebalikan dari User → Objects) ──
  const [objectPivot, setObjectPivot]   = useState(null);  // [{object, userCount}]
  const [objPivotBusy, setObjPivotBusy] = useState(false);
  const [expandedObject, setExpandedObject] = useState(null);
  const [objectUsers, setObjectUsers]   = useState({});    // object → {users, loading, error}
  const [ouUserFilter, setOuUserFilter] = useState('');
  const [objModal, setObjModal]     = useState(null);      // { username, object } modal detail object
  const [objModalData, setObjModalData] = useState(null);  // { description, roles }
  const [objModalBusy, setObjModalBusy] = useState(false);
  const [userRoleDrawer, setUserRoleDrawer] = useState(null); // username → drawer role user (User→Role)
  const [tcodeFilter, setTcodeFilter] = useState({});         // filter tcode per user (User→Tcode)
  const [detailBackUser, setDetailBackUser] = useState(null); // asal drawer detail (username) → tombol kembali
  const [tcodeDrawer, setTcodeDrawer] = useState(null);       // tcode → drawer user+role terkait
  const [tcodeDrawerData, setTcodeDrawerData] = useState(null);
  const [tcodeDrawerBusy, setTcodeDrawerBusy] = useState(false);
  const [tcodeUserFilter, setTcodeUserFilter] = useState({}); // filter user di dalam tcode (Tcode→User)
  const [tcUserDrawer, setTcUserDrawer] = useState(null);     // {tcode, username} → role irisan
  const [tcUserData, setTcUserData] = useState(null);
  const [tcUserBusy, setTcUserBusy] = useState(false);
  // Tahap 2 (write): panel Kelola Role di drawer user.
  const [manageUser, setManageUser] = useState(null);   // username yang sedang dikelola role-nya
  const [manageRoles, setManageRoles] = useState(null); // daftar role live dari SAP [{agr,from,to}]
  const [manageBusy, setManageBusy] = useState(false);
  const [manageErr, setManageErr] = useState(null);
  const [removeSet, setRemoveSet] = useState(new Set()); // role yang akan dihapus
  const [addSet, setAddSet] = useState([]);              // role yang akan ditambah (nama)
  const [addInput, setAddInput] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Tahap 2 (write): editor authorization object di dalam role.
  const [authEditFor, setAuthEditFor] = useState(null);   // role yang authnya diedit
  const [authChanges, setAuthChanges] = useState([]);     // change-set [{action,objekt,auth,field,low,high,deleteField}]
  const [authGenerate, setAuthGenerate] = useState(true);
  const [authUserCompare, setAuthUserCompare] = useState(false);
  const [authAllowProd, setAuthAllowProd] = useState(false);     // opt-in edit langsung di client produktif
  const [authConfirm, setAuthConfirm] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authNewObj, setAuthNewObj] = useState({ objekt: '', field: '', low: '', high: '' });
  const [fileEditorOpen, setFileEditorOpen] = useState(false);   // editor value berbasis file PFCG
  const [filePreset, setFilePreset] = useState(null);            // { name, base64 } hasil download dari SAP
  const [search, setSearch]   = useState('');

  const [detailFor, setDetailFor] = useState(null);   // nama role utk drawer detail
  const [detail, setDetail]       = useState(null);   // { users, tcodes, children }
  const [detailBusy, setDetailBusy] = useState(false);
  // ── Role History (change document PFCG) ──
  const _ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [histOpen, setHistOpen]   = useState(false);
  const [histFrom, setHistFrom]   = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return _ymd(d); });
  const [histTo, setHistTo]       = useState(() => _ymd(new Date()));
  const [histRole, setHistRole]   = useState('');
  const [histRows, setHistRows]   = useState(null);   // null = belum dimuat
  const [histBusy, setHistBusy]   = useState(false);
  const [histErr, setHistErr]     = useState(null);
  const [histRange, setHistRange] = useState(null);   // {from,to} dari server
  const [histOpenCn, setHistOpenCn] = useState(null); // changenr yang detailnya dibuka
  const [histDetail, setHistDetail] = useState({});   // changenr → items[]
  const [histDetailBusy, setHistDetailBusy] = useState(null);
  const [detailTab, setDetailTab] = useState('users');
  const [roleMenu, setRoleMenu] = useState(null);        // { role, tree, count } menu PFCG role
  const [roleMenuBusy, setRoleMenuBusy] = useState(false);
  const [detailMax, setDetailMax] = useState(false);  // drawer detail role: maximize toggle
  const [tcodeMax, setTcodeMax] = useState(false);     // drawer tcode: maximize toggle
  const [userRoleMax, setUserRoleMax] = useState(false); // drawer user→roles: maximize toggle

  useEffect(() => {
    api.get('/api/sap/connections').then(({ data }) => {
      const list = data || [];
      setProfiles(list);
      // Auto-pilih profil PRD (client 310) bila ada & belum ada pilihan — biar
      // langsung ke sistem produksi tanpa pilih manual.
      if (!connId && list.length) {
        const norm = (v) => String(v || '').trim().toUpperCase();
        const prd = list.find(p => (norm(p.landscape) === 'PRD' || norm(p.environment) === 'PRD' || norm(p.sid) === 'PRD') && norm(p.client) === '310')
                 || list.find(p => norm(p.landscape) === 'PRD' || norm(p.environment) === 'PRD' || norm(p.sid) === 'PRD')
                 || list.find(p => norm(p.client) === '310');
        if (prd) setConnId(String(prd.id));
      }
    }).catch(() => setProfiles([]));
  }, []); // eslint-disable-line

  const loadAssignments = useCallback((cid) => {
    api.get(`/api/sap/roles/assignments?connectionId=${cid}`)
      .then(({ data }) => setAssigns(data.assignments || []))
      .catch(() => setAssigns([]));
    api.get(`/api/sap/roles/user-tcodes?connectionId=${cid}`)
      .then(({ data }) => setUserTcodes(data.users || []))
      .catch(() => setUserTcodes([]));
    api.get(`/api/sap/roles/tcode-desc?connectionId=${cid}`)
      .then(({ data }) => setTcodeDesc(data.map || {}))
      .catch(() => setTcodeDesc({}));
  }, []);

  // Snapshot dari DB saat profil dipilih (instan, tanpa hit SAP).
  useEffect(() => {
    if (!connId) return;
    api.get(`/api/sap/roles/cached?connectionId=${connId}`).then(({ data }) => {
      if (data.roles?.length) {
        setRoles(data.roles); setSnapshotAt(data.snapshotAt); setFromCache(true);
        loadAssignments(connId);
      }
    }).catch(() => {});
  }, [connId, loadAssignments]);

  const profile = useMemo(() => profiles.find(p => String(p.id) === String(connId)) || null, [profiles, connId]);
  const canList = profile && profile.pathReadtable && profile.hasPassword;

  const apiErr = (e) => {
    const d = e?.response?.data;
    const base = d?.error || e?.message || (t('sapRole.errorOccurred'));
    return d?.detail ? `${base} — ${d.detail}` : base;
  };

  const loadRoles = useCallback(async () => {
    if (!connId) return;
    setLoading(true); setBanner(null); setRoles(null); setMeta(null); setDetailFor(null);
    try {
      const { data } = await api.post('/api/sap/roles/detail-list', { connectionId: Number(connId), usedOnly });
      setRoles(data.roles || []);
      setMeta(data.meta || null);
      setSnapshotAt(data.snapshotAt || null);
      setFromCache(false);
      loadAssignments(connId);
      if (!data.roles?.length) setBanner({ type: 'info', text: t('sapRole.noRolesFound') });
    } catch (e) {
      setBanner({ type: 'error', text: apiErr(e) });
    } finally { setLoading(false); }
  }, [connId, id, loadAssignments, usedOnly]);

  // ── Insight (audit) ────────────────────────────────────────────────────────
  const insight = useMemo(() => {
    const rs = roles || [];
    const orphan = rs.filter(r => r.userCount === 0 && !r.isComposite);
    const expired = [], expiring = [];
    for (const a of assigns) {
      if (isUnlimited(a.toDate)) continue;
      const dd = daysUntil(a.toDate);
      if (dd === null) continue;
      if (dd < 0) expired.push(a);
      else if (dd <= 30) expiring.push(a);
    }
    return {
      total: rs.length,
      composite: rs.filter(r => r.isComposite).length,
      derived: rs.filter(r => !!r.parentAgr).length,
      orphan, expired, expiring,
    };
  }, [roles, assigns]);

  // ── Pivot User → Roles ────────────────────────────────────────────────────
  const userPivot = useMemo(() => {
    const m = {};
    for (const a of assigns) {
      const u = m[a.username] || (m[a.username] = { username: a.username, roles: [], expired: 0 });
      u.roles.push(a);
      if (!isUnlimited(a.toDate) && (daysUntil(a.toDate) ?? 1) < 0) u.expired++;
    }
    return Object.values(m).sort((x, y) => y.roles.length - x.roles.length || x.username.localeCompare(y.username));
  }, [assigns]);

  // ── Pivot Role → User (hanya user aktif; assigns sudah difilter di backend) ──
  const rolePivot = useMemo(() => {
    const descBy = {};
    (roles || []).forEach(r => { descBy[r.name] = r.description || ''; });
    const m = {};
    for (const a of assigns) {
      const r = m[a.role] || (m[a.role] = { role: a.role, description: descBy[a.role] || '', users: [], expired: 0 });
      r.users.push(a);
      if (!isUnlimited(a.toDate) && (daysUntil(a.toDate) ?? 1) < 0) r.expired++;
    }
    // "Only assigned roles" off -> tampilkan juga role tanpa user (mis. role
    // baru/dummy), supaya tetap bisa dibuka dan di-edit authorization-nya.
    if (!usedOnly) {
      (roles || []).forEach(r => { if (r.name && !m[r.name]) m[r.name] = { role: r.name, description: r.description || '', users: [], expired: 0 }; });
    }
    return Object.values(m).sort((x, y) => y.users.length - x.users.length || x.role.localeCompare(y.role));
  }, [assigns, roles, usedOnly]);

  // ── Filter + sort daftar role ─────────────────────────────────────────────
  // (Tab Role disembunyikan — halaman fokus User→Role & User→Tcode. Blok filter
  //  role dihapus; insight cards tetap memakai `insight`.)

  const filteredUsers = useMemo(() => {
    const q = search.trim().toUpperCase();
    return q ? userPivot.filter(u => u.username.toUpperCase().includes(q)) : userPivot;
  }, [userPivot, search]);

  const filteredUserTcodes = useMemo(() => {
    const q = search.trim().toUpperCase();
    return q ? userTcodes.filter(u => u.username.toUpperCase().includes(q)) : userTcodes;
  }, [userTcodes, search]);

  const filteredRolePivot = useMemo(() => {
    const q = search.trim().toUpperCase();
    return q ? rolePivot.filter(r => r.role.toUpperCase().includes(q) || (r.description || '').toUpperCase().includes(q)) : rolePivot;
  }, [rolePivot, search]);

  const roleDescBy = useMemo(() => {
    const m = {};
    (roles || []).forEach(r => { m[r.name] = r.description || ''; });
    return m;
  }, [roles]);

  // ── Pivot Tcode → User: balik dari userTcodes (sudah active-only & hanya tcode
  //    yang dimiliki user). Tcode tak terpakai otomatis tak muncul. ──
  const tcodePivot = useMemo(() => {
    const m = {};
    for (const u of userTcodes) {
      for (const t of u.tcodes) (m[t] = m[t] || []).push(u.username);
    }
    return Object.entries(m)
      .map(([tcode, users]) => ({ tcode, users: users.sort() }))
      .sort((a, b) => b.users.length - a.users.length || a.tcode.localeCompare(b.tcode));
  }, [userTcodes]);

  const filteredTcodePivot = useMemo(() => {
    const q = search.trim().toUpperCase();
    return q ? tcodePivot.filter(t => t.tcode.toUpperCase().includes(q)) : tcodePivot;
  }, [tcodePivot, search]);

  const filteredUserObjects = useMemo(() => {
    const list = userObjects || [];
    const q = search.trim().toUpperCase();
    return q ? list.filter(u => u.username.toUpperCase().includes(q)) : list;
  }, [userObjects, search]);

  // List ringan dari cache (cepat). Dipanggil saat tab dibuka & setelah build.
  const fetchObjectList = useCallback(async () => {
    if (!connId) return;
    setObjListBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/user-objects?connectionId=${connId}`);
      setUserObjects(data.users || []);
      setObjBuilt(!!data.built);
      setObjBuiltAt(data.builtAt || null);
      if (data.built) {
        api.get(`/api/sap/roles/object-desc?connectionId=${connId}`)
          .then(({ data: d }) => setObjDesc(d.map || {})).catch(() => {});
      }
    } catch (e) {
      setUserObjects([]); setObjNote(apiErr(e));
    } finally { setObjListBusy(false); }
  }, [connId]); // eslint-disable-line

  // Build cache: baca AGR_1251 sekali → simpan ke tabel. Berat, jadi eksplisit.
  const buildObjects = useCallback(async () => {
    if (!connId) return;
    setObjBusy(true); setObjNote(null);
    try {
      await api.post('/api/sap/roles/objects/build', { connectionId: Number(connId) });
      await fetchObjectList();
    } catch (e) {
      setObjNote(apiErr(e));
    } finally { setObjBusy(false); }
  }, [connId, fetchObjectList]); // eslint-disable-line

  // Detail object+field 1 user (lazy dari cache) saat user diklik.
  const toggleUser = useCallback(async (username) => {
    setObjFilter('');
    if (expandedUser === username) { setExpandedUser(null); return; }
    setExpandedUser(username);
    if (userDetail[username]) return;
    setUserDetail(prev => ({ ...prev, [username]: { loading: true } }));
    try {
      const { data } = await api.get(`/api/sap/roles/user-objects/detail?connectionId=${connId}&username=${encodeURIComponent(username)}`);
      setUserDetail(prev => ({ ...prev, [username]: { objects: data.objects || [] } }));
    } catch (e) {
      setUserDetail(prev => ({ ...prev, [username]: { objects: [], error: apiErr(e) } }));
    }
  }, [connId, expandedUser, userDetail]); // eslint-disable-line

  // Auto-muat list ringan saat tab User→Object dibuka pertama kali.
  useEffect(() => {
    if (tab === 'userobject' && connId && userObjects === null && !objListBusy) fetchObjectList();
  }, [tab, connId, userObjects, objListBusy, fetchObjectList]);

  // ── Object → User: list object + jumlah user, lalu user per object (lazy) ──
  const fetchObjectPivot = useCallback(async () => {
    if (!connId) return;
    setObjPivotBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/objects?connectionId=${connId}`);
      setObjectPivot(data.objects || []);
      setObjBuilt(!!data.built);
      setObjBuiltAt(data.builtAt || null);
      if (data.built) {
        api.get(`/api/sap/roles/object-desc?connectionId=${connId}`)
          .then(({ data: d }) => setObjDesc(d.map || {})).catch(() => {});
      }
    } catch (e) {
      setObjectPivot([]); setObjNote(apiErr(e));
    } finally { setObjPivotBusy(false); }
  }, [connId]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'objectuser' && connId && objectPivot === null && !objPivotBusy) fetchObjectPivot();
  }, [tab, connId, objectPivot, objPivotBusy, fetchObjectPivot]);

  // Reset batas render saat pindah tab / ganti kata kunci pencarian.
  useEffect(() => { setListLimit(LIST_STEP); }, [tab, search]);

  const filteredObjectPivot = useMemo(() => {
    const q = search.trim().toUpperCase();
    const list = objectPivot || [];
    return q ? list.filter(o => o.object.toUpperCase().includes(q) || (objDesc[o.object] || '').toUpperCase().includes(q)) : list;
  }, [objectPivot, search, objDesc]);

  const filteredRoleObjPivot = useMemo(() => {
    const q = search.trim().toUpperCase();
    const list = rolePivotObj || [];
    return q ? list.filter(r => r.agrName.toUpperCase().includes(q) || (roleDescBy[r.agrName] || '').toUpperCase().includes(q)) : list;
  }, [rolePivotObj, search, roleDescBy]);

  const filteredObjRolePivot = useMemo(() => {
    const q = search.trim().toUpperCase();
    const list = objRolePivot || [];
    return q ? list.filter(o => o.object.toUpperCase().includes(q) || (objDesc[o.object] || '').toUpperCase().includes(q)) : list;
  }, [objRolePivot, search, objDesc]);

  const toggleObject = useCallback(async (object) => {
    if (expandedObject === object) { setExpandedObject(null); return; }
    setExpandedObject(object); setOuUserFilter('');
    if (objectUsers[object] && !objectUsers[object].error) return;
    setObjectUsers(prev => ({ ...prev, [object]: { users: [], loading: true } }));
    try {
      const { data } = await api.get(`/api/sap/roles/object-users?connectionId=${connId}&object=${encodeURIComponent(object)}`);
      setObjectUsers(prev => ({ ...prev, [object]: { users: data.users || [], loading: false } }));
    } catch (e) {
      setObjectUsers(prev => ({ ...prev, [object]: { users: [], error: apiErr(e) } }));
    }
  }, [connId, expandedObject, objectUsers]); // eslint-disable-line

  // ── Role ⇄ Object: list dari cache yang sama (sap_rm_role_objfields) ──
  const fetchRoleObjPivot = useCallback(async () => {
    if (!connId) return;
    setRoPivotBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/role-objects?connectionId=${connId}`);
      setRolePivotObj(data.roles || []);
      setObjBuilt(!!data.built); setObjBuiltAt(data.builtAt || null);
    } catch (e) { setRolePivotObj([]); setObjNote(apiErr(e)); }
    finally { setRoPivotBusy(false); }
  }, [connId]); // eslint-disable-line

  const fetchObjRolePivot = useCallback(async () => {
    if (!connId) return;
    setOrPivotBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/object-roles?connectionId=${connId}`);
      setObjRolePivot(data.objects || []);
      setObjBuilt(!!data.built); setObjBuiltAt(data.builtAt || null);
      if (data.built) {
        api.get(`/api/sap/roles/object-desc?connectionId=${connId}`)
          .then(({ data: d }) => setObjDesc(d.map || {})).catch(() => {});
      }
    } catch (e) { setObjRolePivot([]); setObjNote(apiErr(e)); }
    finally { setOrPivotBusy(false); }
  }, [connId]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'roleobject' && connId && rolePivotObj === null && !roPivotBusy) fetchRoleObjPivot();
    if (tab === 'objectrole' && connId && objRolePivot === null && !orPivotBusy) fetchObjRolePivot();
  }, [tab, connId, rolePivotObj, objRolePivot, roPivotBusy, orPivotBusy, fetchRoleObjPivot, fetchObjRolePivot]);

  const toggleRoleObj = useCallback(async (agrName) => {
    if (expandedRoleObj === agrName) { setExpandedRoleObj(null); return; }
    setExpandedRoleObj(agrName);
    if (roleObjDetail[agrName] && !roleObjDetail[agrName].error) return;
    setRoleObjDetail(prev => ({ ...prev, [agrName]: { objects: [], loading: true } }));
    try {
      const { data } = await api.get(`/api/sap/roles/role-objects/detail?connectionId=${connId}&agrName=${encodeURIComponent(agrName)}`);
      setRoleObjDetail(prev => ({ ...prev, [agrName]: { objects: data.objects || [], loading: false } }));
    } catch (e) {
      setRoleObjDetail(prev => ({ ...prev, [agrName]: { objects: [], error: apiErr(e) } }));
    }
  }, [connId, expandedRoleObj, roleObjDetail]); // eslint-disable-line

  const toggleObjRole = useCallback(async (object) => {
    if (expandedObjRole === object) { setExpandedObjRole(null); return; }
    setExpandedObjRole(object);
    if (objRoleDetail[object] && !objRoleDetail[object].error) return;
    setObjRoleDetail(prev => ({ ...prev, [object]: { roles: [], loading: true } }));
    try {
      const { data } = await api.get(`/api/sap/roles/object-roles/detail?connectionId=${connId}&object=${encodeURIComponent(object)}`);
      setObjRoleDetail(prev => ({ ...prev, [object]: { roles: data.roles || [], loading: false } }));
    } catch (e) {
      setObjRoleDetail(prev => ({ ...prev, [object]: { roles: [], error: apiErr(e) } }));
    }
  }, [connId, expandedObjRole, objRoleDetail]); // eslint-disable-line

  // Modal: klik object → field/value + semua role user yang memuat object itu.
  const openObjModal = useCallback(async (username, object) => {
    setObjModal({ username, object }); setObjModalData(null); setObjModalBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/user-object-roles?connectionId=${connId}&username=${encodeURIComponent(username)}&object=${encodeURIComponent(object)}`);
      setObjModalData(data);
    } catch (e) {
      setObjModalData({ description: '', roles: [], error: apiErr(e) });
    } finally { setObjModalBusy(false); }
  }, [connId]); // eslint-disable-line

  // Drawer tcode: klik tcode → daftar user aktif + role yang memuat tcode itu.
  const openTcodeDrawer = useCallback(async (tcode) => {
    setTcodeDrawer(tcode); setTcodeDrawerData(null); setTcodeDrawerBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/tcode-info?connectionId=${connId}&tcode=${encodeURIComponent(tcode)}`);
      setTcodeDrawerData(data);
    } catch (e) {
      setTcodeDrawerData({ description: '', users: [], roles: [], error: apiErr(e) });
    } finally { setTcodeDrawerBusy(false); }
  }, [connId]); // eslint-disable-line

  // Drawer irisan tcode×user: role yang memuat tcode DAN dimiliki user itu.
  const openTcUserDrawer = useCallback(async (tcode, username) => {
    setTcUserDrawer({ tcode, username }); setTcUserData(null); setTcUserBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/tcode-user-roles?connectionId=${connId}&tcode=${encodeURIComponent(tcode)}&username=${encodeURIComponent(username)}`);
      setTcUserData(data);
    } catch (e) {
      setTcUserData({ description: '', roles: [], error: apiErr(e) });
    } finally { setTcUserBusy(false); }
  }, [connId]); // eslint-disable-line

  // ── Kelola Role (write) ────────────────────────────────────────────────────
  const openManage = useCallback(async (username) => {
    setManageUser(username); setManageRoles(null); setManageErr(null);
    setRemoveSet(new Set()); setAddSet([]); setAddInput(''); setManageBusy(true);
    try {
      const { data } = await api.get(`/api/sap/roles/user-current-roles?connectionId=${connId}&username=${encodeURIComponent(username)}`);
      setManageRoles(data.roles || []);
    } catch (e) {
      setManageErr(apiErr(e)); setManageRoles([]);
    } finally { setManageBusy(false); }
  }, [connId]); // eslint-disable-line

  const toggleRemove = (agr) => setRemoveSet(prev => {
    const n = new Set(prev); n.has(agr) ? n.delete(agr) : n.add(agr); return n;
  });
  const addRoleToAdd = () => {
    const v = addInput.trim().toUpperCase();
    if (!v) return;
    const existing = new Set((manageRoles || []).map(r => r.agr));
    if (existing.has(v) && !removeSet.has(v)) { setAddInput(''); return; } // sudah punya
    if (!addSet.includes(v)) setAddSet(prev => [...prev, v]);
    setAddInput('');
  };

  // Daftar role final setelah perubahan (untuk preview).
  const finalRolesPreview = useMemo(() => {
    const base = (manageRoles || []).map(r => r.agr).filter(a => !removeSet.has(a));
    return [...new Set([...base, ...addSet])].sort();
  }, [manageRoles, removeSet, addSet]);

  const hasChanges = removeSet.size > 0 || addSet.length > 0;

  const commitAssign = useCallback(async (allowEmpty = false) => {
    setCommitting(true); setManageErr(null);
    try {
      const { data } = await api.post('/api/sap/roles/assign', {
        connectionId: Number(connId), username: manageUser,
        add: addSet, remove: [...removeSet], allowEmpty,
      });
      // Refresh daftar live & reset perubahan; sinkron assigns lokal via reload assignments.
      setConfirmOpen(false);
      setBanner({ type: 'success', text: t('sapRole.rolesManageuserUpdated', {
        manageUser
      }) });
      loadAssignments(connId);
      await openManage(manageUser);
    } catch (e) {
      const d = e?.response?.data;
      if (d?.wouldEmpty) { setManageErr(t('sapRole.removesAllRolesTickAllow')); }
      else setManageErr(apiErr(e));
      setConfirmOpen(false);
    } finally { setCommitting(false); }
  }, [connId, manageUser, addSet, removeSet, id, loadAssignments, openManage]); // eslint-disable-line

  // ── Edit Authorization (write) ─────────────────────────────────────────────
  // prefill (optional): { objekt, auth, field, low, high } — dari klik chip field di tab Auth (gaya PFCG).
  // Change-set yang sudah ada dipertahankan bila drawer dibuka lagi untuk role yang sama.
  const openAuthEdit = (role, prefill) => {
    const same = authEditFor === role;
    setAuthEditFor(role);
    if (!same) { setAuthChanges([]); setAuthGenerate(true); setAuthUserCompare(false); setAuthAllowProd(false); }
    const p = prefill || {};
    setAuthNewObj({ objekt: p.objekt || '', field: p.field || '', low: p.low || '', high: p.high || '',
      newLow: p.low || '', newHigh: p.high || '', auth: p.auth || '' });
  };

  // Jalur "Get .SAP from SAP" DIHAPUS: PRGN_DOWNLOAD_AGR CREATE_FILE_ONLY
  // TIDAK menghasilkan format download PFCG (bukan byte-identik). Alur baru:
  // download .SAP manual dari PFCG -> buka di editor value ini -> upload balik.
  const addAuthChange = (chg) => setAuthChanges(prev => [...prev, chg]);
  const removeAuthChange = (i) => setAuthChanges(prev => prev.filter((_, idx) => idx !== i));

  const commitAuth = useCallback(async (test = false) => {
    setAuthBusy(true); setManageErr(null);
    try {
      const { data } = await api.post('/api/sap/roles/modify-auth', {
        connectionId: Number(connId), role: authEditFor, changes: authChanges,
        generate: authGenerate, userCompare: authUserCompare, test, allowProd: authAllowProd,
      });
      if (test) {
        setBanner({ type: 'info', text: (t('sapRole.simulationOk')) + (data.messages || []).map(m => m.message).join('; ') });
      } else {
        setAuthConfirm(false); setAuthEditFor(null); setAuthChanges([]);
        setBanner({ type: 'success', text: (id ? `Authorization role ${authEditFor} diperbarui${data.generated ? ' + profil di-generate' : ''}.` : `Authorizations for ${authEditFor} updated${data.generated ? ' + profile generated' : ''}.`) });
        if (detailFor === authEditFor) openDetail(authEditFor);  // refresh tab Auth
      }
    } catch (e) {
      // Tampilkan juga pesan SAP (ET_RETURN), bukan hanya error generik.
      const msgs = (e?.response?.data?.messages || []).map(m => `${m.type}: ${m.message}`).join(' · ');
      setBanner({ type: 'error', text: msgs ? `${apiErr(e)} — ${msgs}` : apiErr(e) });
      setAuthConfirm(false);
    } finally { setAuthBusy(false); }
  }, [connId, authEditFor, authChanges, authGenerate, authUserCompare, authAllowProd, id, detailFor]); // eslint-disable-line

  // ── Detail role (drawer) ──────────────────────────────────────────────────
  const openDetail = useCallback(async (name) => {
    setRoleWriteMsg(null); setDetailFor(name); setDetail(null); setDetailBusy(true); setDetailTab('users'); setDetailBackUser(null); setRoleMenu(null);
    try {
      const { data } = await api.get(`/api/sap/roles/detail?connectionId=${connId}&name=${encodeURIComponent(name)}`);
      setDetail(data);
    } catch (e) {
      setDetail({ users: [], tcodes: [], children: [], error: apiErr(e) });
    } finally { setDetailBusy(false); }
  }, [connId]);

  // ── Edit langsung dari modal role: Users (assign/remove) & Tcodes/Menu ─────
  const [roleWriteBusy, setRoleWriteBusy] = useState(false);
  const [roleWriteMsg, setRoleWriteMsg] = useState(null);   // pesan hasil, tampil DI DALAM panel role
  const refreshDetailKeepTab = useCallback(async (name) => {
    setRoleMenu(null);   // paksa menu dimuat ulang
    try {
      const { data } = await api.get(`/api/sap/roles/detail?connectionId=${connId}&name=${encodeURIComponent(name)}`);
      setDetail(data);
    } catch (e) { /* biarkan detail lama */ }
  }, [connId]);
  const curProfile = () => profiles.find(p => String(p.id) === String(connId)) || {};
  const profLabel = (p) => `${p.landscape || ''}${p.environment ? '/' + p.environment : ''} ${p.sid || ''}/${p.client || ''}`.trim();
  const isProdProfile = (p) => /^(PRD|PROD)/i.test(String(p.environment || ''));

  const roleUserChange = useCallback(async (username, op) => {
    const role = detailFor; const p = curProfile(); if (!role || !username) return;
    const msg = op === 'add'
      ? `Assign role ${role} to user ${username} in ${profLabel(p)}?`
      : `Remove role ${role} from user ${username} in ${profLabel(p)}?`;
    if (!window.confirm(msg + (isProdProfile(p) ? '\n\nThis is a PRODUCTIVE system.' : ''))) return;
    setRoleWriteBusy(true);
    const body = { connectionId: Number(connId), username, add: op === 'add' ? [role] : [], remove: op === 'remove' ? [role] : [] };
    try {
      try { await api.post('/api/sap/roles/assign', body); }
      catch (e) {
        if (e?.response?.data?.wouldEmpty && window.confirm(`${username} would have NO roles left. Continue anyway?`)) {
          await api.post('/api/sap/roles/assign', { ...body, allowEmpty: true });
        } else throw e;
      }
      { const m = { type: 'success', text: op === 'add' ? `Role ${role} assigned to ${username}.` : `Role ${role} removed from ${username}.` }; setBanner(m); setRoleWriteMsg(m); }
      loadAssignments(connId);
      await refreshDetailKeepTab(role);
    } catch (e) {
      const msgs = (e?.response?.data?.messages || []).map(m => `${m.type}: ${m.message}`).join(' · ');
      { const m = { type: 'error', text: msgs ? `${apiErr(e)} — ${msgs}` : apiErr(e) }; setBanner(m); setRoleWriteMsg(m); }
    } finally { setRoleWriteBusy(false); }
  }, [connId, detailFor, profiles]); // eslint-disable-line

  const roleTcodeChange = useCallback(async (tcode, op) => {
    const role = detailFor; const p = curProfile(); if (!role || !tcode) return;
    const prod = isProdProfile(p);
    const msg = op === 'add'
      ? `Add tcode ${tcode} to role ${role} (menu + S_TCODE) and generate, in ${profLabel(p)}?`
      : `Remove tcode ${tcode} from role ${role} (menu + S_TCODE) and generate, in ${profLabel(p)}?`;
    if (!window.confirm(msg + (prod ? '\n\nThis is a PRODUCTIVE client — the change is applied directly (Allow productive client).' : ''))) return;
    setRoleWriteBusy(true);
    try {
      const { data } = await api.post('/api/sap/roles/modify-auth', {
        connectionId: Number(connId), role, generate: true, allowProd: prod,
        changes: [{ action: op === 'add' ? 'T' : 'X', objekt: 'S_TCODE', field: 'TCD', low: tcode }],
      });
      const msgs = (data.messages || []).map(m => m.message).join(' · ');
      { const m = { type: 'success', text: `${op === 'add' ? 'Added' : 'Removed'} ${tcode} ${op === 'add' ? 'to' : 'from'} ${role}${data.generated ? ' + profile generated' : ''}. ${msgs}` }; setBanner(m); setRoleWriteMsg(m); }
      await refreshDetailKeepTab(role);
    } catch (e) {
      const msgs = (e?.response?.data?.messages || []).map(m => `${m.type}: ${m.message}`).join(' · ');
      { const m = { type: 'error', text: msgs ? `${apiErr(e)} — ${msgs}` : apiErr(e) }; setBanner(m); setRoleWriteMsg(m); }
    } finally { setRoleWriteBusy(false); }
  }, [connId, detailFor, profiles]); // eslint-disable-line

  // ── Create / copy / delete role (FM aksi N / C / K) ─────────────────────────
  const roleAdminOp = useCallback(async (op, target, low) => {
    const p = curProfile(); const prod = isProdProfile(p);
    const tgt = String(target || '').trim().toUpperCase(); if (!tgt) return;
    setRoleWriteBusy(true);
    try {
      const { data } = await api.post('/api/sap/roles/modify-auth', {
        connectionId: Number(connId), role: tgt, generate: true, allowProd: prod,
        changes: [{ action: op, objekt: '', field: '', low: low || '' }],
      });
      const msgs = (data.messages || []).map(m => m.message).join(' · ');
      const m = { type: 'success', text: `${op === 'N' ? 'Created' : op === 'C' ? 'Copied' : 'Deleted'} role ${tgt}. ${msgs}` };
      setBanner(m); setRoleWriteMsg(m);
      if (op === 'K') {
        setRoles(prev => (prev || []).filter(x => x.name !== tgt));
        setDetailFor(null);
      } else {
        setRoles(prev => (prev || []).some(x => x.name === tgt) ? prev
          : [{ name: tgt, description: op === 'N' ? (low || '') : `Copy of ${low}`, isComposite: false }, ...(prev || [])]);
        openDetail(tgt);
      }
    } catch (e) {
      const msgs = (e?.response?.data?.messages || []).map(m => `${m.type}: ${m.message}`).join(' · ');
      const m = { type: 'error', text: msgs ? `${apiErr(e)} — ${msgs}` : apiErr(e) }; setBanner(m); setRoleWriteMsg(m);
    } finally { setRoleWriteBusy(false); }
  }, [connId, profiles]); // eslint-disable-line

  const promptNewRole = () => {
    const p = curProfile();
    const name = (window.prompt(`New single role in ${profLabel(p)}\nRole name (must start with Y or Z):`) || '').trim().toUpperCase();
    if (!name) return;
    if (!/^[YZ][A-Z0-9_\/]{1,29}$/.test(name)) { setBanner({ type: 'error', text: 'Role name must start with Y or Z (A-Z, 0-9, _ ; max 30 chars).' }); return; }
    const desc = (window.prompt(`Description for ${name}:`) || '').trim();
    if (!window.confirm(`Create empty role ${name} in ${profLabel(p)}?` + (isProdProfile(p) ? '\n\nThis is a PRODUCTIVE client.' : ''))) return;
    roleAdminOp('N', name, desc);
  };
  const promptCopyRole = (src) => {
    const p = curProfile();
    const name = (window.prompt(`Copy role ${src} in ${profLabel(p)} (user assignments are NOT copied)\nNew role name (must start with Y or Z):`) || '').trim().toUpperCase();
    if (!name) return;
    if (!/^[YZ][A-Z0-9_\/]{1,29}$/.test(name)) { setBanner({ type: 'error', text: 'Role name must start with Y or Z (A-Z, 0-9, _ ; max 30 chars).' }); return; }
    if (!window.confirm(`Copy ${src} → ${name} and generate the profile?` + (isProdProfile(p) ? '\n\nThis is a PRODUCTIVE client.' : ''))) return;
    roleAdminOp('C', name, src);
  };
  const promptDeleteRole = (name) => {
    const p = curProfile();
    const typed = (window.prompt(`DELETE role ${name} in ${profLabel(p)}?` + (isProdProfile(p) ? '\nThis is a PRODUCTIVE client.' : '') + `\nThe role must not be assigned to any user.\n\nType the role name to confirm:`) || '').trim().toUpperCase();
    if (!typed) return;
    if (typed !== name) { setBanner({ type: 'error', text: 'Role name did not match — nothing deleted.' }); return; }
    roleAdminOp('K', name, '');
  };

  // ── Role History: baca change document (CDHDR) sesuai rentang & filter role ──
  const loadHistory = useCallback(async () => {
    if (!connId) return;
    setHistBusy(true); setHistErr(null);
    try {
      const params = { connectionId: Number(connId), from: histFrom, to: histTo };
      if (histRole.trim()) params.role = histRole.trim();
      const { data } = await api.get('/api/sap/roles/history', { params });
      setHistRows(data.items || []);
      setHistRange({ from: data.from, to: data.to });
      setHistOpenCn(null); setHistDetail({});
    } catch (e) {
      setHistRows([]); setHistErr(apiErr(e));
    } finally { setHistBusy(false); }
  }, [connId, histFrom, histTo, histRole]);

  // Detail satu perubahan (CDPOS) on-demand; toggle baris.
  const toggleHistDetail = useCallback(async (changenr) => {
    if (histOpenCn === changenr) { setHistOpenCn(null); return; }
    setHistOpenCn(changenr);
    if (histDetail[changenr]) return;               // sudah pernah dimuat
    setHistDetailBusy(changenr);
    try {
      const { data } = await api.get('/api/sap/roles/history/detail', { params: { connectionId: Number(connId), changenr } });
      setHistDetail(prev => ({ ...prev, [changenr]: data.items || [] }));
    } catch (e) {
      setHistDetail(prev => ({ ...prev, [changenr]: { error: apiErr(e) } }));
    } finally { setHistDetailBusy(null); }
  }, [connId, histOpenCn, histDetail]);

  // Buka panel history + auto-load pertama kali.
  const openHistory = useCallback(() => {
    setHistOpen(o => {
      const next = !o;
      if (next && histRows === null && connId) loadHistory();
      return next;
    });
  }, [histRows, connId, loadHistory]);

  // Lazy-load menu PFCG saat tab "Menu" dibuka (per role).
  useEffect(() => {
    if (detailTab !== 'menu' || !detailFor || !connId) return;
    if (roleMenu && roleMenu.role === detailFor) return;
    let cancel = false;
    setRoleMenuBusy(true);
    api.get(`/api/sap/roles/menu?connectionId=${connId}&role=${encodeURIComponent(detailFor)}`)
      .then(({ data }) => { if (!cancel) setRoleMenu({ role: detailFor, ...data }); })
      .catch(e => { if (!cancel) setRoleMenu({ role: detailFor, tree: [], count: 0, error: apiErr(e) }); })
      .finally(() => { if (!cancel) setRoleMenuBusy(false); });
    return () => { cancel = true; };
  }, [detailTab, detailFor, connId]); // eslint-disable-line

  const detailRole = useMemo(() => (roles || []).find(r => r.name === detailFor) || null, [roles, detailFor]);

  // ── Konteks AI halaman ────────────────────────────────────────────────────
  const aiSummary = useMemo(() => {
    if (!roles) return '';
    const lines = [];
    lines.push('SAP Role Maintenance — ringkasan role & assignment (read-only, sumber AGR_*).');
    if (profile) lines.push(`Sistem: ${profile.sid || '?'} (${profile.landscape || ''}/${profile.environment || ''}, host ${profile.host || '?'}, client ${profile.client || '?'}).`);
    lines.push(`TOTAL ROLE: ${insight.total}. Composite: ${insight.composite}. Derived: ${insight.derived}. Orphan (single, 0 user): ${insight.orphan.length}. Assignment expired: ${insight.expired.length}. Akan expire ≤30 hari: ${insight.expiring.length}.`);
    if (insight.orphan.length) {
      const l = insight.orphan.slice(0, 200).map(r => r.name);
      lines.push(`ROLE ORPHAN: ${l.join(', ')}${insight.orphan.length > 200 ? ` …(+${insight.orphan.length - 200} lagi)` : ''}`);
    }
    if (insight.expiring.length) {
      const l = insight.expiring.slice(0, 200).map(a => `${a.username}→${a.role} (s/d ${a.toDate})`);
      lines.push(`ASSIGNMENT AKAN EXPIRE ≤30 HARI: ${l.join('; ')}${insight.expiring.length > 200 ? ` …(+${insight.expiring.length - 200} lagi)` : ''}`);
    }
    const top = userPivot.slice(0, 30).map(u => `${u.username}: ${u.roles.length}`);
    if (top.length) lines.push(`USER DENGAN ROLE TERBANYAK (top 30): ${top.join(', ')}`);
    return lines.join('\n');
  }, [roles, profile, insight, userPivot]);

  usePageAIContext('sap_roles', 'SAP Role Maintenance', aiSummary);

  // ── Sub-komponen kecil ────────────────────────────────────────────────────
  const StatCard = ({ icon: Icon, label, value, tone = 'slate', onClick, active }) => (
    <button
      onClick={onClick} disabled={!onClick}
      className={`rounded-2xl border p-4 text-left shadow-sm transition-all bg-white
        ${active ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200'}
        ${onClick ? 'hover:border-blue-300 cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`text-${tone}-500`} style={{ width: 15, height: 15 }} />
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
    </button>
  );

  const AssignBadge = ({ toDate }) => {
    const {
      t
    } = useLang();

    if (isUnlimited(toDate)) return null;
    const dd = daysUntil(toDate);
    if (dd === null) return null;
    if (dd < 0)  return <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-600 border border-rose-200">{id ? 'expired' : 'expired'}</span>;
    if (dd <= 30) return (
      <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">{t('sapRole.ddDays', {
        dd
      })}</span>
    );
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Info read-only */}
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
        <Info className="text-blue-500 flex-shrink-0 mt-0.5" style={{ width: 18, height: 18 }} />
        <p className="text-xs text-blue-700 leading-relaxed">
          {t('sapRole.pageReadOnlyPhase1')}
        </p>
        <span className={`ml-auto shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold border ${canWrite ? 'border-rose-300 text-rose-600 bg-rose-50' : 'border-slate-300 text-slate-500 bg-white'}`}>
          {canWrite ? 'Access: Modify' : 'Access: Read-only'}
        </span>
      </div>
      {/* Pemilih profil */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Server className="text-blue-500" style={{ width: 16, height: 16 }} />
          <h3 className="font-display font-bold text-slate-800 text-sm">{t('sapRole.selectSapSystem')}</h3>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">{t('sapRole.profileSapConfig')}</label>
            <select
              value={connId}
              onChange={(e) => { setConnId(e.target.value); setRoles(null); setAssigns([]); setUserTcodes([]); setUserObjects(null); setObjectPivot(null); setExpandedObject(null); setObjectUsers({}); setRolePivotObj(null); setObjRolePivot(null); setRoleObjDetail({}); setObjRoleDetail({}); setObjBuilt(false); setObjBuiltAt(null); setObjNote(null); setExpandedUser(null); setUserDetail({}); setObjFilter(''); setTcodeDesc({}); setObjDesc({}); setObjModal(null); setUserRoleDrawer(null); setTcodeFilter({}); setTcodeDrawer(null); setTcodeUserFilter({}); setTcUserDrawer(null); setManageUser(null); setConfirmOpen(false); setAuthEditFor(null); setAuthConfirm(false); setBanner(null); setSnapshotAt(null); setFromCache(false); setDetailFor(null); setSearch(''); setTab('roleuser'); }}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white outline-none focus:border-blue-300"
            >
              <option value="">{t('sapRole.selectProfile')}</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.landscape}{p.environment ? ` / ${p.environment}` : ''}{p.sid ? ` — ${p.sid}` : ''}{p.client ? `/${p.client}` : ''}{p.host ? ` (${p.host})` : ''}{canWrite && p.pathAuthmod ? ' · auth edit' : ''}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 select-none cursor-pointer pb-2">
            <input type="checkbox" checked={usedOnly} onChange={(e) => setUsedOnly(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-300" />
            {t('sapRole.onlyAssignedRoles')}
          </label>
          {canWrite && connId && (
            <button onClick={promptNewRole} disabled={roleWriteBusy}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
              New role
            </button>
          )}
          {canWrite && (
            <button onClick={() => setFileEditorOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600">
              {t('sapRole.valueEditorPfcgFile')}
            </button>
          )}
          <button
            onClick={loadRoles} disabled={!canList || loading}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all
              ${(!canList || loading) ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}
          >
            <RefreshCw style={{ width: 15, height: 15 }} className={loading ? 'animate-spin' : ''} />
            {loading ? (t('sapRole.loading')) : (t('sapRole.loadRoles'))}
          </button>
        </div>
        {profile && (
          <div className="mt-3 text-xs">
            {!profile.hasPassword && <p className="text-rose-600 mb-1">{t('sapRole.profileHasNoPasswordSet')}</p>}
            {profile && !profile.pathReadtable && (
              <p className="text-amber-600">{t('sapRole.rfcReadTableServicePath')}</p>
            )}
            {snapshotAt && (
              <p className="text-slate-400 mt-1">
                {id ? 'Snapshot' : 'Snapshot'}: {new Date(snapshotAt).toLocaleString()} {fromCache ? (t('sapRole.cacheClickLoadRolesRefresh')) : ''}
                {meta?.agr_define_total != null && (
                  <span className="ml-1">
                    · {t('sapRole.showing')} {meta.agr_define_used ?? '?'} {t('sapRole.of')} {meta.agr_define_total} {t('sapRole.rolesAssigned')}
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </div>
      {/* Banner */}
      {banner && (
        <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm
          ${banner.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : banner.type === 'error' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-blue-200 bg-blue-50 text-blue-700'}`}>
          <AlertCircle style={{ width: 16, height: 16 }} className="flex-shrink-0 mt-0.5" />
          <span>{banner.text}</span>
        </div>
      )}
      {meta && Object.keys(meta.errors || {}).length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          {t('sapRole.someTablesFailedRead')}
          {Object.entries(meta.errors).map(([k, v]) => `${k.toUpperCase()} (${v})`).join('; ')}
        </div>
      )}
      {roles && (
        <>
          {/* Insight cards (display-only) */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <StatCard icon={ShieldCheck} label={t('sapRole.totalRoles')} value={insight.total} tone="blue" />
            <StatCard icon={Layers} label="Composite" value={insight.composite} tone="violet" />
            <StatCard icon={GitBranch} label="Derived" value={insight.derived} tone="sky" />
            <StatCard icon={UserX} label={t('sapRole.orphan0Users')} value={insight.orphan.length} tone="rose" />
            <StatCard icon={CalendarClock} label={t('sapRole.expiring30Days')} value={`${insight.expiring.length}`} tone="amber" />
            <StatCard icon={History} label="Role History" value={histRange ? (histRows?.length ?? 0) : '…'} tone="emerald" onClick={openHistory} active={histOpen} />
          </div>

          {/* ── Panel Role History (change document PFCG) ── */}
          {histOpen && (
            <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-slate-800 flex items-center gap-2">
                  <History size={16} className="text-emerald-500" />
                  {t('sapRole.roleObjectChangeHistory')}
                </h3>
                <button onClick={() => setHistOpen(false)} className="p-1 rounded hover:bg-emerald-100 text-slate-500"><X size={16} /></button>
              </div>
              <div className="flex flex-wrap items-end gap-2 mb-3 text-xs">
                <label className="flex flex-col gap-1">
                  <span className="text-slate-500">{t('sapRole.from')}</span>
                  <input type="date" value={histFrom} onChange={e => setHistFrom(e.target.value)} className="border border-slate-300 rounded px-2 py-1" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-500">{t('sapRole.to')}</span>
                  <input type="date" value={histTo} onChange={e => setHistTo(e.target.value)} className="border border-slate-300 rounded px-2 py-1" />
                </label>
                <label className="flex flex-col gap-1 flex-1 min-w-[160px]">
                  <span className="text-slate-500">{t('sapRole.roleFilterPrefixOptional')}</span>
                  <input value={histRole} onChange={e => setHistRole(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadHistory()}
                    placeholder={t('sapRole.eGZEmptyAll')}
                    className="border border-slate-300 rounded px-2 py-1 uppercase" />
                </label>
                <button onClick={loadHistory} disabled={histBusy}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3 py-1.5 font-semibold">
                  <RefreshCw size={13} className={histBusy ? 'animate-spin' : ''} />
                  {histBusy ? (t('sapRole.loading')) : (t('sapRole.load'))}
                </button>
              </div>

              {histErr && (
                <div className="mb-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{histErr}</span>
                </div>
              )}
              {histRange && !histErr && (
                <p className="text-[11px] text-slate-500 mb-2">
                  {t('sapRole.period')} {histRange.from} – {histRange.to} · {histRows?.length || 0} {t('sapRole.changes')}
                  {(histRows?.length || 0) >= 500 ? (t('sapRole.limitedLatest500')) : ''}
                </p>
              )}

              <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-slate-500">
                    <tr className="text-left">
                      <th className="px-2 py-2 w-6"></th>
                      <th className="px-2 py-2">{t('sapRole.date')}</th>
                      <th className="px-2 py-2">{id ? 'Role' : 'Role'}</th>
                      <th className="px-2 py-2">User</th>
                      <th className="px-2 py-2">Tcode</th>
                      <th className="px-2 py-2">{t('sapRole.type')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(histRows || []).map((r) => {
                      const open = histOpenCn === r.changenr;
                      const det = histDetail[r.changenr];
                      return (
                        <React.Fragment key={r.changenr}>
                          <tr onClick={() => toggleHistDetail(r.changenr)} className="border-t border-slate-100 hover:bg-emerald-50/60 cursor-pointer">
                            <td className="px-2 py-1.5 text-slate-400">
                              <ChevronRight size={13} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{r.date} {r.time}</td>
                            <td className="px-2 py-1.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); openDetail(r.role); }}
                                className="font-semibold text-blue-600 hover:text-blue-800 hover:underline text-left"
                                title={t('sapRole.openRoleDetail')}
                              >{r.role}</button>
                            </td>
                            <td className="px-2 py-1.5 text-slate-600">{r.user}</td>
                            <td className="px-2 py-1.5 text-slate-500">{r.tcode}</td>
                            <td className="px-2 py-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                r.changeInd === 'I' ? 'bg-blue-100 text-blue-700'
                                : r.changeInd === 'D' || r.changeInd === 'E' ? 'bg-rose-100 text-rose-700'
                                : 'bg-amber-100 text-amber-700'}`}>{r.changeText}</span>
                            </td>
                          </tr>
                          {open && (
                            <tr className="bg-slate-50/70">
                              <td></td>
                              <td colSpan={5} className="px-2 py-2">
                                {histDetailBusy === r.changenr ? (
                                  <span className="text-slate-400">{t('sapRole.readingDetailCdpos')}</span>
                                ) : det && det.error ? (
                                  <span className="text-rose-600">{det.error}</span>
                                ) : det && det.length ? (
                                  <div className="overflow-auto">
                                    <table className="w-full text-[11px]">
                                      <thead className="text-slate-400">
                                        <tr className="text-left">
                                          <th className="px-2 py-1">{t('sapRole.table')}</th>
                                          <th className="px-2 py-1">Field</th>
                                          <th className="px-2 py-1">{t('sapRole.ind')}</th>
                                          <th className="px-2 py-1">{t('sapRole.oldValue')}</th>
                                          <th className="px-2 py-1">{t('sapRole.newValue')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {det.map((d, i) => (
                                          <tr key={i} className="border-t border-slate-200">
                                            <td className="px-2 py-1 font-mono text-slate-600">{d.table}</td>
                                            <td className="px-2 py-1 font-mono text-slate-600">{d.field}</td>
                                            <td className="px-2 py-1 text-slate-500">{d.chngText}</td>
                                            <td className="px-2 py-1 font-mono text-rose-600 break-all">{d.valueOld || '—'}</td>
                                            <td className="px-2 py-1 font-mono text-emerald-700 break-all">{d.valueNew || '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">{t('sapRole.noFieldDetailEmptyCdpos')}</span>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {histRows && !histRows.length && !histBusy && (
                      <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">
                        {t('sapRole.noChangesPeriodPfcgChange')}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tab + toolbar */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100">
              <div className="flex flex-wrap items-center gap-1 rounded-2xl bg-slate-100/80 p-1 text-[13px] font-semibold">
                {TAB_PAIRS.map((g) => {
                  const active = tab === g.a || tab === g.b;
                  const cur = tab === g.b ? g.b : g.a;
                  const other = cur === g.a ? g.b : g.a;
                  const counts = {
                    roleuser: filteredRolePivot.length,   users: filteredUsers.length,
                    usertcode: filteredUserTcodes.length, tcodeuser: filteredTcodePivot.length,
                    userobject: userObjects ? filteredUserObjects.length : null,
                    objectuser: objectPivot ? filteredObjectPivot.length : null,
                    roleobject: rolePivotObj ? filteredRoleObjPivot.length : null,
                    objectrole: objRolePivot ? filteredObjRolePivot.length : null,
                  };
                  const n = counts[cur];
                  return (
                    <div key={g.key}
                      className={`flex items-center rounded-xl transition-colors ${active ? 'bg-white shadow-sm ring-1 ring-slate-200/80' : ''}`}>
                      <button type="button" onClick={() => setTab(cur)}
                        className={`pl-3 ${active ? 'pr-1.5' : 'pr-3'} py-1.5 rounded-xl transition-colors ${active ? 'text-blue-700' : 'text-slate-500 hover:text-slate-800'}`}>
                        {DIR_LABEL[cur]}
                        {n === null || n === undefined ? '' : (
                          <span className={`ml-1.5 font-normal ${active ? 'text-blue-400' : 'text-slate-400'}`}>{n}</span>
                        )}
                      </button>
                      {active && (
                        <button type="button" onClick={() => setTab(other)}
                          title={`Switch to ${DIR_LABEL[other]}`} aria-label={`Switch to ${DIR_LABEL[other]}`}
                          className="mr-1.5 p-1 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition-colors">
                          <ArrowLeftRight style={{ width: 13, height: 13 }} />
                        </button>
                      )}
                    </div>
                  );
                })}
                <span className="mx-0.5 h-5 w-px bg-slate-200" />
                {[
                  { k: 'sod', label: 'SoD Conflicts' },
                  { k: 'license', label: 'License Audit' },
                  { k: 'fue', label: 'FUE Simulation' },
                ].map((x) => (
                  <button key={x.k} type="button" onClick={() => setTab(x.k)}
                    className={`px-3 py-1.5 rounded-xl transition-colors ${tab === x.k
                      ? 'bg-white text-blue-700 shadow-sm ring-1 ring-slate-200/80'
                      : 'text-slate-500 hover:text-slate-800'}`}>
                    {x.label}
                  </button>
                ))}
              </div>
              {(tab === 'sod' || tab === 'license' || tab === 'fue') && (
                <div className="mt-4">
                  {tab === 'fue' ? <FuePanel connId={connId} /> : (tab === 'license' ? <LicensePanel connId={connId} /> : <SodPanel connId={connId} />)}
                </div>
              )}
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" style={{ width: 14, height: 14 }} />
                <input
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder={tab === 'roleuser' ? (t('sapRole.searchRoleDescription'))
                    : tab === 'tcodeuser' ? (t('sapRole.searchTcode'))
                    : tab === 'objectuser' || tab === 'objectrole' ? 'Search authorization object…'
                    : tab === 'roleobject' ? (t('sapRole.searchRoleDescription'))
                    : (t('sapRole.searchUsername'))}
                  className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300"
                />
              </div>
            </div>

            {/* ── Tab: Role → User (hanya user aktif) ── */}
            {tab === 'roleuser' && (
              <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                {filteredRolePivot.map(r => (
                  <details key={r.role} className="group">
                    <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50 list-none">
                      <span className="min-w-0">
                        <button onClick={(e) => { e.preventDefault(); openDetail(r.role); }}
                          className="font-semibold text-slate-700 text-sm hover:text-blue-600 text-left truncate">
                          {r.role}
                        </button>
                        {r.description && <span className="block text-[11px] text-slate-400 truncate">{r.description}</span>}
                      </span>
                      <span className="text-xs text-slate-500 shrink-0 ml-2">
                        {r.users.length} user
                        {r.expired > 0 && <span className="ml-2 text-rose-500 font-semibold">{r.expired} expired</span>}
                      </span>
                    </summary>
                    <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                      {r.users.map(a => (
                        <span key={a.username} className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-600 border border-slate-200">
                          {a.username}<AssignBadge toDate={a.toDate} />
                        </span>
                      ))}
                    </div>
                  </details>
                ))}
                {!filteredRolePivot.length && (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">{t('sapRole.noRolesActiveUsersNot')}</div>
                )}
              </div>
            )}

            {/* ── Tab: User → Roles (baris expand chips; klik nama → drawer) ── */}
            {tab === 'users' && (
              <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                {filteredUsers.map(u => (
                  <details key={u.username} className="group">
                    <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50 list-none">
                      <button onClick={(e) => { e.preventDefault(); setUserRoleDrawer(u.username); }}
                        className="font-semibold text-slate-700 text-sm hover:text-blue-600 text-left">
                        {u.username}
                      </button>
                      <span className="text-xs text-slate-500 shrink-0 ml-2">
                        {u.roles.length} {t('sapRole.roles')}
                        {u.expired > 0 && <span className="ml-2 text-rose-500 font-semibold">{u.expired} expired</span>}
                      </span>
                    </summary>
                    <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                      {u.roles.map(a => (
                        <button key={a.role} onClick={() => openDetail(a.role)}
                          className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600">
                          {a.role}<AssignBadge toDate={a.toDate} />
                        </button>
                      ))}
                    </div>
                  </details>
                ))}
                {!filteredUsers.length && (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">{t('sapRole.noAssignmentsNotLoadedYet')}</div>
                )}
              </div>
            )}

            {/* ── Tab: User → Tcode (hanya user aktif) ── */}
            {tab === 'usertcode' && (
              <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                {filteredUserTcodes.map(u => {
                  const q = (tcodeFilter[u.username] || '').trim().toUpperCase();
                  const shown = q ? u.tcodes.filter(t => t.toUpperCase().includes(q) || (tcodeDesc[t] || '').toUpperCase().includes(q)) : u.tcodes;
                  return (
                    <details key={u.username} className="group">
                      <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50 list-none">
                        <span className="font-semibold text-slate-700 text-sm">{u.username}</span>
                        <span className="text-xs text-slate-500">{u.tcodes.length} tcode</span>
                      </summary>
                      <div className="px-4 pb-3">
                        {u.tcodes.length > 20 && (
                          <input
                            value={tcodeFilter[u.username] || ''}
                            onChange={(e) => setTcodeFilter(prev => ({ ...prev, [u.username]: e.target.value }))}
                            placeholder={t('sapRole.searchTcode')}
                            className="w-full mb-2 px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-300"
                          />
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {shown.map(t => (
                            <button key={t} onClick={() => openTcUserDrawer(t, u.username)}
                              title={tcodeDesc[t] ? `${tcodeDesc[t]} — ${id ? 'lihat role terkait' : 'view related roles'}` : (id ? 'Lihat role terkait' : 'View related roles')}
                              className="inline-flex px-2 py-1 rounded-lg text-[11px] font-mono bg-slate-50 text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600">{t}</button>
                          ))}
                          {!shown.length && <span className="text-[11px] text-slate-400">{u.tcodes.length ? (t('sapRole.noMatchingTcodes')) : (t('sapRole.noTcodes'))}</span>}
                        </div>
                      </div>
                    </details>
                  );
                })}
                {!filteredUserTcodes.length && (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">{t('sapRole.noDataNotLoadedYet')}</div>
                )}
              </div>
            )}

            {/* ── Tab: Tcode → User (hanya tcode yang dipakai user aktif) ── */}
            {tab === 'tcodeuser' && (
              <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                {filteredTcodePivot.slice(0, listLimit).map(t => {
                  const q = (tcodeUserFilter[t.tcode] || '').trim().toUpperCase();
                  const shown = q ? t.users.filter(u => u.toUpperCase().includes(q)) : t.users;
                  return (
                    <details key={t.tcode} className="group">
                      <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50 list-none">
                        <span className="min-w-0">
                          <button onClick={(e) => { e.preventDefault(); openTcodeDrawer(t.tcode); }}
                            className="font-mono font-semibold text-slate-700 text-sm hover:text-blue-600 text-left">{t.tcode}</button>
                          {tcodeDesc[t.tcode] && <span className="block text-[11px] text-slate-400 truncate">{tcodeDesc[t.tcode]}</span>}
                        </span>
                        <span className="text-xs text-slate-500 shrink-0 ml-2">{t.users.length} user</span>
                      </summary>
                      <div className="px-4 pb-3">
                        {t.users.length > 20 && (
                          <input
                            value={tcodeUserFilter[t.tcode] || ''}
                            onChange={(e) => setTcodeUserFilter(prev => ({ ...prev, [t.tcode]: e.target.value }))}
                            placeholder={id ? 'Cari user…' : 'Search user…'}
                            className="w-full mb-2 px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-300"
                          />
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {shown.map(u => (
                            <button key={u} onClick={() => openTcUserDrawer(t.tcode, u)}
                              className="inline-flex px-2 py-1 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600">{u}</button>
                          ))}
                          {!shown.length && <span className="text-[11px] text-slate-400">{id ? 'Tidak ada user yang cocok.' : 'No matching users.'}</span>}
                        </div>
                      </div>
                    </details>
                  );
                })}
                {filteredTcodePivot.length > listLimit && (
                  <button type="button" onClick={() => setListLimit((l) => l + 300)}
                    className="w-full px-4 py-2.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/60">
                    Show more ({filteredTcodePivot.length - listLimit} left)
                  </button>
                )}
                {!filteredTcodePivot.length && (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">{t('sapRole.noTcodesNotLoadedYet')}</div>
                )}
              </div>
            )}

            {/* ── Tab: User → Object (cache; detail lazy per user) ── */}
            {tab === 'userobject' && (
              <div>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <button
                    onClick={buildObjects} disabled={objBusy}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-all
                      ${objBusy ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}
                  >
                    <RefreshCw style={{ width: 14, height: 14 }} className={objBusy ? 'animate-spin' : ''} />
                    {objBusy ? (t('sapRole.readingAgr1251')) : (objBuilt ? (t('sapRole.rebuildCache')) : (t('sapRole.buildObjectCache')))}
                  </button>
                  <span className="text-xs text-slate-400">
                    {objBuilt
                      ? (id ? `Cache: ${objBuiltAt ? new Date(objBuiltAt).toLocaleString() : '—'} · klik user untuk lihat object` : `Cache: ${objBuiltAt ? new Date(objBuiltAt).toLocaleString() : '—'} · click a user to view objects`)
                      : (t('sapRole.noCacheYetBuildFirst'))}
                  </span>
                </div>
                {objNote && <div className="px-4 py-2 text-xs text-amber-600">{objNote}</div>}
                <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
                  {objListBusy && <div className="px-4 py-6 text-center text-slate-400 text-sm">{t('sapRole.loadingList')}</div>}
                  {!objListBusy && !filteredUserObjects.length && (
                    <div className="px-4 py-8 text-center text-slate-400 text-sm">
                      {objBuilt ? (t('sapRole.noUsersObjects')) : (t('sapRole.clickBuildObjectCacheStart'))}
                    </div>
                  )}
                  {!objListBusy && filteredUserObjects.map(u => {
                    const open = expandedUser === u.username;
                    const det = userDetail[u.username];
                    const objs = det?.objects || [];
                    const q = objFilter.trim().toUpperCase();
                    const shown = q ? objs.filter(o => o.object.toUpperCase().includes(q)) : objs;
                    return (
                      <div key={u.username}>
                        <button onClick={() => toggleUser(u.username)}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left">
                          <span className="font-semibold text-slate-700 text-sm">{u.username}</span>
                          <span className="text-xs text-slate-500 shrink-0 ml-2">{u.objectCount} object</span>
                        </button>
                        {open && (
                          <div className="px-4 pb-3 bg-slate-50/40">
                            {det?.loading && <p className="text-xs text-slate-400 py-2">{t('sapRole.loadingObjects')}</p>}
                            {det?.error && <p className="text-xs text-rose-500 py-2">{det.error}</p>}
                            {det && !det.loading && (
                              <>
                                {objs.length > 20 && (
                                  <input
                                    value={objFilter} onChange={(e) => setObjFilter(e.target.value)}
                                    placeholder={t('sapRole.searchObject')}
                                    className="w-full mb-2 px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-300"
                                  />
                                )}
                                <div className="space-y-1">
                                  {shown.map(o => (
                                    <button key={o.object} onClick={() => openObjModal(u.username, o.object)}
                                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left hover:border-blue-300 hover:bg-blue-50/40">
                                      <span className="min-w-0">
                                        <span className="font-mono text-[11px] font-semibold text-slate-700">{o.object}</span>
                                        {objDesc[o.object] && <span className="block text-[10px] text-slate-400 truncate">{objDesc[o.object]}</span>}
                                      </span>
                                      <span className="text-[10px] text-slate-400 shrink-0">{o.fields.length} field ›</span>
                                    </button>
                                  ))}
                                  {!shown.length && <p className="text-[11px] text-slate-400 py-1">{t('sapRole.noMatchingObjects')}</p>}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'objectuser' && (
              <div>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <button
                    onClick={buildObjects} disabled={objBusy}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-all
                      ${objBusy ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}
                  >
                    <RefreshCw style={{ width: 14, height: 14 }} className={objBusy ? 'animate-spin' : ''} />
                    {objBusy ? (t('sapRole.readingAgr1251')) : (objBuilt ? (t('sapRole.rebuildCache')) : (t('sapRole.buildObjectCache')))}
                  </button>
                  <span className="text-xs text-slate-400">
                    {objBuilt
                      ? `Cache: ${objBuiltAt ? new Date(objBuiltAt).toLocaleString() : '—'} · click an object to view users`
                      : (t('sapRole.noCacheYetBuildFirst'))}
                  </span>
                </div>
                {objNote && <div className="px-4 py-2 text-xs text-amber-600">{objNote}</div>}
                <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
                  {objPivotBusy && <div className="px-4 py-6 text-center text-slate-400 text-sm">{t('sapRole.loadingList')}</div>}
                  {!objPivotBusy && !filteredObjectPivot.length && (
                    <div className="px-4 py-8 text-center text-slate-400 text-sm">
                      {objBuilt ? 'No authorization object matches.' : (t('sapRole.clickBuildObjectCacheStart'))}
                    </div>
                  )}
                  {!objPivotBusy && filteredObjectPivot.slice(0, listLimit).map(o => {
                    const open = expandedObject === o.object;
                    const det = objectUsers[o.object];
                    const users = det?.users || [];
                    const q = ouUserFilter.trim().toUpperCase();
                    const shown = q ? users.filter(u => u.username.toUpperCase().includes(q)) : users;
                    return (
                      <div key={o.object}>
                        <button onClick={() => toggleObject(o.object)}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left">
                          <span className="min-w-0">
                            <span className="font-mono font-semibold text-slate-700 text-sm">{o.object}</span>
                            {objDesc[o.object] && <span className="block text-[10px] text-slate-400 truncate">{objDesc[o.object]}</span>}
                          </span>
                          <span className="text-xs text-slate-500 shrink-0 ml-2">{o.userCount} user</span>
                        </button>
                        {open && (
                          <div className="px-4 pb-3 bg-slate-50/40">
                            {det?.loading && <p className="text-xs text-slate-400 py-2">Loading users…</p>}
                            {det?.error && <p className="text-xs text-rose-500 py-2">{det.error}</p>}
                            {det && !det.loading && (
                              <>
                                {users.length > 20 && (
                                  <input
                                    value={ouUserFilter} onChange={(e) => setOuUserFilter(e.target.value)}
                                    placeholder="Search user…"
                                    className="w-full mb-2 px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-300"
                                  />
                                )}
                                <div className="space-y-1">
                                  {shown.map(u => (
                                    <button key={u.username} onClick={() => openObjModal(u.username, o.object)}
                                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left hover:border-blue-300 hover:bg-blue-50/40">
                                      <span className="min-w-0">
                                        <span className="font-mono text-[11px] font-semibold text-slate-700">{u.username}</span>
                                        <span className="block text-[10px] text-slate-400 truncate">{(u.roles || []).join(', ')}</span>
                                      </span>
                                      <span className="text-[10px] text-slate-400 shrink-0">{(u.roles || []).length} role ›</span>
                                    </button>
                                  ))}
                                  {!shown.length && <p className="text-[11px] text-slate-400 py-1">No matching user.</p>}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredObjectPivot.length > listLimit && (
                    <button type="button" onClick={() => setListLimit((l) => l + 300)}
                      className="w-full px-4 py-2.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/60">
                      Show more ({filteredObjectPivot.length - listLimit} left)
                    </button>
                  )}
                </div>
              </div>
            )}

            {tab === 'roleobject' && (
              <div>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <button onClick={buildObjects} disabled={objBusy}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-all
                      ${objBusy ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}>
                    <RefreshCw style={{ width: 14, height: 14 }} className={objBusy ? 'animate-spin' : ''} />
                    {objBusy ? (t('sapRole.readingAgr1251')) : (objBuilt ? (t('sapRole.rebuildCache')) : (t('sapRole.buildObjectCache')))}
                  </button>
                  <span className="text-xs text-slate-400">
                    {objBuilt
                      ? `Cache: ${objBuiltAt ? new Date(objBuiltAt).toLocaleString() : '—'} · click a role to view objects`
                      : (t('sapRole.noCacheYetBuildFirst'))}
                  </span>
                </div>
                <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
                  {roPivotBusy && <div className="px-4 py-6 text-center text-slate-400 text-sm">Loading list…</div>}
                  {!roPivotBusy && !filteredRoleObjPivot.length && (
                    <div className="px-4 py-8 text-center text-slate-400 text-sm">{objBuilt ? 'No role matches.' : (t('sapRole.clickBuildObjectCacheStart'))}</div>
                  )}
                  {!roPivotBusy && filteredRoleObjPivot.slice(0, listLimit).map(r => {
                    const open = expandedRoleObj === r.agrName;
                    const det = roleObjDetail[r.agrName];
                    return (
                      <div key={r.agrName}>
                        <button onClick={() => toggleRoleObj(r.agrName)}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left">
                          <span className="min-w-0">
                            <span className="font-semibold text-slate-700 text-sm">{r.agrName}</span>
                            {roleDescBy[r.agrName] && <span className="block text-[10px] text-slate-400 truncate">{roleDescBy[r.agrName]}</span>}
                          </span>
                          <span className="text-xs text-slate-500 shrink-0 ml-2">{r.objectCount} object</span>
                        </button>
                        {open && (
                          <div className="px-4 pb-3 bg-slate-50/40">
                            {det?.loading && <p className="text-xs text-slate-400 py-2">Loading objects…</p>}
                            {det?.error && <p className="text-xs text-rose-500 py-2">{det.error}</p>}
                            {det && !det.loading && (
                              <div className="space-y-1">
                                {det.objects.slice(0, 300).map(o => (
                                  <div key={o.object} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                                    <span className="min-w-0">
                                      <span className="font-mono text-[11px] font-semibold text-slate-700">{o.object}</span>
                                      {objDesc[o.object] && <span className="block text-[10px] text-slate-400 truncate">{objDesc[o.object]}</span>}
                                    </span>
                                    <span className="text-[10px] text-slate-400 shrink-0">{o.fields.length} field</span>
                                  </div>
                                ))}
                                {det.objects.length > 300 && <p className="text-[11px] text-slate-400 py-1">…{det.objects.length - 300} more</p>}
                                {!det.objects.length && <p className="text-[11px] text-slate-400 py-1">No object.</p>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredRoleObjPivot.length > listLimit && (
                    <button type="button" onClick={() => setListLimit((l) => l + 300)}
                      className="w-full px-4 py-2.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/60">
                      Show more ({filteredRoleObjPivot.length - listLimit} left)
                    </button>
                  )}
                </div>
              </div>
            )}

            {tab === 'objectrole' && (
              <div>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <button onClick={buildObjects} disabled={objBusy}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-all
                      ${objBusy ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}>
                    <RefreshCw style={{ width: 14, height: 14 }} className={objBusy ? 'animate-spin' : ''} />
                    {objBusy ? (t('sapRole.readingAgr1251')) : (objBuilt ? (t('sapRole.rebuildCache')) : (t('sapRole.buildObjectCache')))}
                  </button>
                  <span className="text-xs text-slate-400">
                    {objBuilt
                      ? `Cache: ${objBuiltAt ? new Date(objBuiltAt).toLocaleString() : '—'} · click an object to view roles`
                      : (t('sapRole.noCacheYetBuildFirst'))}
                  </span>
                </div>
                <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
                  {orPivotBusy && <div className="px-4 py-6 text-center text-slate-400 text-sm">Loading list…</div>}
                  {!orPivotBusy && !filteredObjRolePivot.length && (
                    <div className="px-4 py-8 text-center text-slate-400 text-sm">{objBuilt ? 'No authorization object matches.' : (t('sapRole.clickBuildObjectCacheStart'))}</div>
                  )}
                  {!orPivotBusy && filteredObjRolePivot.slice(0, listLimit).map(o => {
                    const open = expandedObjRole === o.object;
                    const det = objRoleDetail[o.object];
                    return (
                      <div key={o.object}>
                        <button onClick={() => toggleObjRole(o.object)}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left">
                          <span className="min-w-0">
                            <span className="font-mono font-semibold text-slate-700 text-sm">{o.object}</span>
                            {objDesc[o.object] && <span className="block text-[10px] text-slate-400 truncate">{objDesc[o.object]}</span>}
                          </span>
                          <span className="text-xs text-slate-500 shrink-0 ml-2">{o.roleCount} role</span>
                        </button>
                        {open && (
                          <div className="px-4 pb-3 bg-slate-50/40">
                            {det?.loading && <p className="text-xs text-slate-400 py-2">Loading roles…</p>}
                            {det?.error && <p className="text-xs text-rose-500 py-2">{det.error}</p>}
                            {det && !det.loading && (
                              <div className="space-y-1">
                                {det.roles.slice(0, 300).map(r => (
                                  <button key={r.agrName} onClick={() => openDetail(r.agrName)}
                                    className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left hover:border-blue-300 hover:bg-blue-50/40">
                                    <span className="min-w-0">
                                      <span className="text-[11px] font-semibold text-slate-700">{r.agrName}</span>
                                      {r.description && <span className="block text-[10px] text-slate-400 truncate">{r.description}</span>}
                                    </span>
                                    <span className="text-[10px] text-slate-400 shrink-0">{r.fieldCount} field ›</span>
                                  </button>
                                ))}
                                {det.roles.length > 300 && <p className="text-[11px] text-slate-400 py-1">…{det.roles.length - 300} more</p>}
                                {!det.roles.length && <p className="text-[11px] text-slate-400 py-1">No role.</p>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredObjRolePivot.length > listLimit && (
                    <button type="button" onClick={() => setListLimit((l) => l + 300)}
                      className="w-full px-4 py-2.5 text-xs font-semibold text-blue-600 hover:bg-blue-50/60">
                      Show more ({filteredObjRolePivot.length - listLimit} left)
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {/* ── Drawer: role irisan tcode × user ── */}
      {tcUserDrawer && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={() => setTcUserDrawer(null)}>
          <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-display font-bold text-slate-800 truncate">{tcUserDrawer.username}</h3>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {t('sapRole.rolesTcode')} <span className="font-mono font-semibold text-slate-600">{tcUserDrawer.tcode}</span>
                  {(tcUserData?.description || tcodeDesc[tcUserDrawer.tcode]) ? ` · ${tcUserData?.description || tcodeDesc[tcUserDrawer.tcode]}` : ''}
                </p>
              </div>
              <button onClick={() => setTcUserDrawer(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5">
              {tcUserBusy && <p className="text-sm text-slate-400 py-4 text-center">{t('sapRole.loading')}</p>}
              {tcUserData?.error && <p className="text-sm text-rose-500 py-2">{tcUserData.error}</p>}
              {tcUserData && !tcUserBusy && !tcUserData.error && (
                <>
                  <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.relatedRoles')} ({tcUserData.roles.length})</p>
                  <div className="space-y-1">
                    {tcUserData.roles.map(r => (
                      <button key={r.role} onClick={() => openDetail(r.role)}
                        className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-1.5 text-left hover:border-blue-300 hover:bg-blue-50/40">
                        <span className="min-w-0">
                          <span className="font-mono text-[11px] font-semibold text-slate-700">{r.role}</span>
                          {r.description && <span className="block text-[10px] text-slate-400 truncate">{r.description}</span>}
                        </span>
                        <span className="text-slate-300 text-xs shrink-0">›</span>
                      </button>
                    ))}
                    {!tcUserData.roles.length && <p className="text-sm text-slate-400 py-1">{t('sapRole.noRelatedRoles')}</p>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Drawer: tcode → user aktif + role terkait ── */}
      {tcodeDrawer && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={() => { setTcodeDrawer(null); setTcodeMax(false); }}>
          <div className={`w-full ${tcodeMax ? 'max-w-5xl' : 'max-w-md'} h-full bg-white shadow-2xl overflow-y-auto transition-[max-width] duration-200`} onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-display font-bold text-slate-800 font-mono truncate">{tcodeDrawer}</h3>
                {(tcodeDrawerData?.description || tcodeDesc[tcodeDrawer]) && (
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{tcodeDrawerData?.description || tcodeDesc[tcodeDrawer]}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setTcodeMax(v => !v)}
                  title={tcodeMax ? (t('sapRole.restore')) : (t('sapRole.maximize'))}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                  {tcodeMax ? <Minimize2 style={{ width: 18, height: 18 }} /> : <Maximize2 style={{ width: 18, height: 18 }} />}
                </button>
                <button onClick={() => { setTcodeDrawer(null); setTcodeMax(false); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
              </div>
            </div>
            <div className="p-5">
              {tcodeDrawerBusy && <p className="text-sm text-slate-400 py-4 text-center">{t('sapRole.loading')}</p>}
              {tcodeDrawerData?.error && <p className="text-sm text-rose-500 py-2">{tcodeDrawerData.error}</p>}
              {tcodeDrawerData && !tcodeDrawerBusy && !tcodeDrawerData.error && (
                <>
                  <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.rolesContainingTcode')} ({tcodeDrawerData.roles.length})</p>
                  <div className="space-y-1 mb-5">
                    {tcodeDrawerData.roles.map(r => (
                      <button key={r.role} onClick={() => openDetail(r.role)}
                        className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-1.5 text-left hover:border-blue-300 hover:bg-blue-50/40">
                        <span className="min-w-0">
                          <span className="font-mono text-[11px] font-semibold text-slate-700">{r.role}</span>
                          {r.description && <span className="block text-[10px] text-slate-400 truncate">{r.description}</span>}
                        </span>
                        <span className="text-slate-300 text-xs shrink-0">›</span>
                      </button>
                    ))}
                    {!tcodeDrawerData.roles.length && <p className="text-sm text-slate-400 py-1">{t('sapRole.noRoles')}</p>}
                  </div>
                  <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.activeUsers')} ({tcodeDrawerData.users.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {tcodeDrawerData.users.map(u => (
                      <span key={u} className="inline-flex px-2 py-1 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-600 border border-slate-200">{u}</span>
                    ))}
                    {!tcodeDrawerData.users.length && <p className="text-sm text-slate-400 py-1">{t('sapRole.noUsers')}</p>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {fileEditorOpen && <RoleAuthFileEditor onClose={() => { setFileEditorOpen(false); setFilePreset(null); }} id={id} presetName={filePreset?.name || ''} presetBase64={filePreset?.base64 || ''} />}
      {/* ── Panel Edit Authorization (write) — tahap 2 ── */}
      {authEditFor && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => !authBusy && setAuthEditFor(null)}>
          <div className="w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-slate-800">{t('sapRole.editAuthorizations')} — {authEditFor}</h3>
                <p className="text-xs text-rose-500 mt-0.5">{t('sapRole.writesSapViaZRole')}</p>
              </div>
              <button onClick={() => !authBusy && setAuthEditFor(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5 flex-1">
              {/* Tambah / ubah / hapus */}
              <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.addChange')}</p>
              <div className="rounded-xl border border-slate-200 p-3 mb-3 space-y-2">
                {authNewObj.objekt && authNewObj.field && (
                  <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-2 py-1">
                    {authNewObj.low
                      ? <>Editing <span className="font-mono font-semibold">{authNewObj.objekt} · {authNewObj.field} = {authNewObj.low}{authNewObj.high ? `–${authNewObj.high}` : ''}</span> — change NEW LOW/HIGH then <b>Replace value</b>, or <b>Delete value</b>.</>
                      : <>Field <span className="font-mono font-semibold">{authNewObj.objekt} · {authNewObj.field}</span> is empty — fill LOW (and HIGH) then <b>Insert value</b>.</>}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input value={authNewObj.objekt} onChange={(e) => setAuthNewObj(o => ({ ...o, objekt: e.target.value.toUpperCase() }))}
                    placeholder={t('sapRole.objectEGSTcode')} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono uppercase outline-none focus:border-blue-300" />
                  <input value={authNewObj.field} onChange={(e) => setAuthNewObj(o => ({ ...o, field: e.target.value.toUpperCase() }))}
                    placeholder={t('sapRole.fieldEGActvt')} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono uppercase outline-none focus:border-blue-300" />
                  <input value={authNewObj.low} onChange={(e) => setAuthNewObj(o => ({ ...o, low: e.target.value }))}
                    placeholder="LOW" className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono outline-none focus:border-blue-300" />
                  <input value={authNewObj.high} onChange={(e) => setAuthNewObj(o => ({ ...o, high: e.target.value }))}
                    placeholder="HIGH (optional)" className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono outline-none focus:border-blue-300" />
                  <input value={authNewObj.newLow || ''} onChange={(e) => setAuthNewObj(o => ({ ...o, newLow: e.target.value }))}
                    placeholder="NEW LOW (replace only)" className="px-2 py-1.5 rounded-lg border border-amber-200 bg-amber-50/40 text-xs font-mono outline-none focus:border-amber-400" />
                  <input value={authNewObj.newHigh || ''} onChange={(e) => setAuthNewObj(o => ({ ...o, newHigh: e.target.value }))}
                    placeholder="NEW HIGH (replace, optional)" className="px-2 py-1.5 rounded-lg border border-amber-200 bg-amber-50/40 text-xs font-mono outline-none focus:border-amber-400" />
                  <input value={authNewObj.auth || ''} onChange={(e) => setAuthNewObj(o => ({ ...o, auth: e.target.value.toUpperCase() }))}
                    placeholder="AUTH (optional, e.g. T-D103131500)" className="col-span-2 px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono uppercase outline-none focus:border-blue-300" />
                </div>
                {(() => {
                  const n = authNewObj;
                  const reset = () => setAuthNewObj({ objekt: '', field: '', low: '', high: '', newLow: '', newHigh: '', auth: '' });
                  const base = { objekt: n.objekt, auth: n.auth || '', field: n.field, low: n.low, high: n.high };
                  const acts = [
                    { key: 'I', label: 'Insert value', ok: n.objekt && n.field && n.low,
                      chg: { ...base, action: 'I' }, cls: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' },
                    { key: 'R', label: 'Replace value', ok: n.objekt && n.field && n.low && n.newLow,
                      chg: { ...base, action: 'R', newLow: n.newLow, newHigh: n.newHigh || '' }, cls: 'border-blue-300 text-blue-600 hover:bg-blue-50' },
                    { key: 'DV', label: 'Delete value', ok: n.objekt && n.field && n.low,
                      chg: { ...base, action: 'D' }, cls: 'border-rose-300 text-rose-600 hover:bg-rose-50' },
                    { key: 'DO', label: 'Delete object', ok: n.objekt && !n.field,
                      chg: { action: 'D', objekt: n.objekt, auth: n.auth || '' }, cls: 'border-rose-400 text-rose-700 hover:bg-rose-50' },
                  ];
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {acts.map(a => (
                        <button key={a.key} disabled={!a.ok} onClick={() => { addAuthChange(a.chg); reset(); }}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${a.ok ? a.cls : 'border-slate-200 text-slate-300 cursor-not-allowed'}`}>{a.label}</button>
                      ))}
                    </div>
                  );
                })()}
                <p className="text-[10px] text-slate-400 leading-relaxed">
                  Insert: OBJECT + FIELD + LOW (a new object is created with its other fields left open).
                  Replace: OBJECT + FIELD + current LOW → NEW LOW.
                  Delete value: OBJECT + FIELD + LOW (the last value of a field leaves the field open).
                  Delete object: OBJECT only, FIELD empty. Any error cancels the whole change set.
                </p>
              </div>

              {/* Change-set */}
              <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.changeSet')} ({authChanges.length})</p>
              <div className="space-y-1 mb-3">
                {authChanges.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-1.5">
                    <span className="min-w-0 font-mono text-[11px] text-slate-700 truncate">
                      <span className={`font-bold ${c.action === 'D' ? 'text-rose-500' : c.action === 'I' ? 'text-emerald-600' : 'text-blue-600'}`}>{c.action}</span>
                      {' '}{c.objekt}{c.auth ? ` [${c.auth}]` : ''}{c.field ? ` · ${c.field}` : ''}{(c.low || c.high) ? ` = ${c.low}${c.high ? `–${c.high}` : ''}` : ''}{c.action === 'R' ? ` → ${c.newLow}${c.newHigh ? `–${c.newHigh}` : ''}` : ''}{c.action === 'D' && !c.field ? ' (whole object)' : ''}
                    </span>
                    <button onClick={() => removeAuthChange(i)} className="text-slate-400 hover:text-rose-500 text-xs shrink-0">×</button>
                  </div>
                ))}
                {!authChanges.length && <p className="text-[11px] text-slate-400">{t('sapRole.noChangesYet')}</p>}
              </div>

              <div className="flex items-center gap-4 text-xs">
                <label className="flex items-center gap-1.5 font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={authGenerate} onChange={(e) => setAuthGenerate(e.target.checked)} className="w-4 h-4" />
                  {t('sapRole.generateProfile')}
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={authUserCompare} onChange={(e) => setAuthUserCompare(e.target.checked)} className="w-4 h-4" />
                  {id ? 'User compare (PFUD)' : 'User compare (PFUD)'}
                </label>
                <label className={`flex items-center gap-1.5 font-semibold cursor-pointer ${authAllowProd ? 'text-rose-600' : 'text-slate-600'}`}
                  title="Required for productive clients (e.g. P01). Changes are applied directly in that client, not via transport.">
                  <input type="checkbox" checked={authAllowProd} onChange={(e) => setAuthAllowProd(e.target.checked)} className="w-4 h-4 accent-rose-600" />
                  Allow productive client
                </label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => commitAuth(true)} disabled={!authChanges.length || authBusy}
                className={`px-3 py-2 rounded-xl text-sm font-semibold border ${(!authChanges.length || authBusy) ? 'border-slate-200 text-slate-400' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                {t('sapRole.simulate')}
              </button>
              <button onClick={() => setAuthConfirm(true)} disabled={!authChanges.length || authBusy}
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${(!authChanges.length || authBusy) ? 'bg-slate-100 text-slate-400' : 'bg-rose-600 text-white hover:bg-rose-700'}`}>
                {t('sapRole.applyGenerate')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Konfirmasi Edit Authorization */}
      {authConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => !authBusy && setAuthConfirm(false)}>
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-slate-800 mb-1">{t('sapRole.applySap')}</h3>
            <p className="text-xs text-slate-500 mb-3">{id ? 'Role' : 'Role'} <span className="font-mono font-semibold">{authEditFor}</span> · {authChanges.length} {t('sapRole.changes')}{authGenerate ? (t('sapRole.generateProfile2')) : ''}</p>
            <p className="text-[11px] text-rose-500 mb-3">{t('sapRole.writtenSapImmediatelyLoggedMake')}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAuthConfirm(false)} disabled={authBusy} className="px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">{t('sapRole.cancel')}</button>
              <button onClick={() => commitAuth(false)} disabled={authBusy} className="px-4 py-2 rounded-xl text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700">
                {authBusy ? (t('sapRole.saving')) : (t('sapRole.yesApply'))}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Panel Kelola Role (write) — tahap 2 ── */}
      {manageUser && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => !committing && setManageUser(null)}>
          <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-slate-800">{t('sapRole.manageRoles')} — {manageUser}</h3>
                <p className="text-xs text-rose-500 mt-0.5">{t('sapRole.writesSapTestNonPrd')}</p>
              </div>
              <button onClick={() => !committing && setManageUser(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5 flex-1">
              {manageBusy && <p className="text-sm text-slate-400 py-4 text-center">{t('sapRole.loadingRolesSap')}</p>}
              {manageErr && <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{manageErr}</div>}
              {manageRoles && !manageBusy && (
                <>
                  <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.currentRolesSap')} ({manageRoles.length})</p>
                  <div className="space-y-1 mb-4 max-h-[35vh] overflow-y-auto">
                    {manageRoles.map(r => {
                      const marked = removeSet.has(r.agr);
                      return (
                        <div key={r.agr} className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 ${marked ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50/60'}`}>
                          <span className="min-w-0">
                            <span className={`font-mono text-[11px] font-semibold ${marked ? 'text-rose-500 line-through' : 'text-slate-700'}`}>{r.agr}</span>
                            {roleDescBy[r.agr] && <span className="block text-[10px] text-slate-400 truncate">{roleDescBy[r.agr]}</span>}
                          </span>
                          <button onClick={() => toggleRemove(r.agr)}
                            className={`shrink-0 text-[10px] font-semibold px-2 py-1 rounded-lg border ${marked ? 'border-slate-300 text-slate-500 bg-white' : 'border-rose-300 text-rose-600 hover:bg-rose-50'}`}>
                            {marked ? (t('sapRole.undo')) : (t('sapRole.remove'))}
                          </button>
                        </div>
                      );
                    })}
                    {!manageRoles.length && <p className="text-xs text-slate-400">{t('sapRole.userHasNoRoles')}</p>}
                  </div>

                  <p className="text-xs font-semibold text-slate-500 mb-2">{t('sapRole.addRoles')}</p>
                  <div className="flex gap-2 mb-2">
                    <input value={addInput} onChange={(e) => setAddInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addRoleToAdd(); }}
                      placeholder={t('sapRole.roleNameEGZ')}
                      className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-mono outline-none focus:border-blue-300 uppercase" />
                    <button onClick={addRoleToAdd} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">+</button>
                  </div>
                  {addSet.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {addSet.map(a => (
                        <span key={a} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {a}<button onClick={() => setAddSet(prev => prev.filter(x => x !== a))} className="text-emerald-500 hover:text-emerald-800">×</button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 mb-4">
                    <p className="text-[11px] font-semibold text-slate-500 mb-1">{t('sapRole.finalListPreview')} ({finalRolesPreview.length})</p>
                    <p className="text-[11px] text-slate-500">
                      {removeSet.size > 0 && <span className="text-rose-500">− {removeSet.size} </span>}
                      {addSet.length > 0 && <span className="text-emerald-600">+ {addSet.length}</span>}
                      {!hasChanges && <span className="text-slate-400">{t('sapRole.noChangesYet')}</span>}
                    </p>
                  </div>
                </>
              )}
            </div>
            {manageRoles && !manageBusy && (
              <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
                <button onClick={() => setManageUser(null)} disabled={committing}
                  className="px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">{t('sapRole.close')}</button>
                <button onClick={() => setConfirmOpen(true)} disabled={!hasChanges || committing}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold ${(!hasChanges || committing) ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-rose-600 text-white hover:bg-rose-700'}`}>
                  {t('sapRole.applySap2')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Dialog konfirmasi commit */}
      {confirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => !committing && setConfirmOpen(false)}>
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-slate-800 mb-1">{t('sapRole.confirmRoleChanges')}</h3>
            <p className="text-xs text-slate-500 mb-3">{id ? 'User' : 'User'} <span className="font-semibold">{manageUser}</span></p>
            {removeSet.size > 0 && (
              <div className="mb-2">
                <p className="text-[11px] font-semibold text-rose-500 mb-1">− {t('sapRole.remove')} ({removeSet.size})</p>
                <div className="flex flex-wrap gap-1">{[...removeSet].map(r => <span key={r} className="text-[10px] font-mono bg-rose-50 text-rose-600 border border-rose-200 px-1.5 py-0.5 rounded">{r}</span>)}</div>
              </div>
            )}
            {addSet.length > 0 && (
              <div className="mb-2">
                <p className="text-[11px] font-semibold text-emerald-600 mb-1">+ {t('sapRole.add')} ({addSet.length})</p>
                <div className="flex flex-wrap gap-1">{addSet.map(r => <span key={r} className="text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">{r}</span>)}</div>
              </div>
            )}
            <p className="text-[11px] text-slate-400 my-3">{t('sapRole.finalLengthRolesWrittenSap', {
              length: finalRolesPreview.length
            })}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} disabled={committing}
                className="px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">{t('sapRole.cancel')}</button>
              <button onClick={() => commitAssign(finalRolesPreview.length === 0)} disabled={committing}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700">
                {committing ? (t('sapRole.saving')) : (t('sapRole.yesApply'))}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Drawer: role milik satu user (User→Role) ── */}
      {userRoleDrawer && (() => {
        const u = userPivot.find(x => x.username === userRoleDrawer);
        const rolesList = u ? u.roles : [];
        return (
          <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={() => { setUserRoleDrawer(null); setUserRoleMax(false); }}>
            <div className={`w-full ${userRoleMax ? 'max-w-5xl' : 'max-w-md'} h-full bg-white shadow-2xl overflow-y-auto transition-[max-width] duration-200`} onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
                <div>
                  <h3 className="font-display font-bold text-slate-800">{userRoleDrawer}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{rolesList.length} {t('sapRole.roles')}{u && u.expired > 0 ? ` · ${u.expired} expired` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {canWrite && (
                    <button onClick={() => openManage(userRoleDrawer)}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700">
                      {t('sapRole.manageRoles')}
                    </button>
                  )}
                  <button onClick={() => setUserRoleMax(v => !v)}
                    title={userRoleMax ? (t('sapRole.restore')) : (t('sapRole.maximize'))}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                    {userRoleMax ? <Minimize2 style={{ width: 18, height: 18 }} /> : <Maximize2 style={{ width: 18, height: 18 }} />}
                  </button>
                  <button onClick={() => { setUserRoleDrawer(null); setUserRoleMax(false); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
                </div>
              </div>
              <div className="p-4 divide-y divide-slate-100">
                {rolesList.map(a => (
                  <button key={a.role} onClick={() => { openDetail(a.role); setDetailBackUser(userRoleDrawer); }}
                    className="w-full flex items-center justify-between gap-2 py-2.5 text-left hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                    <span className="min-w-0">
                      <span className="font-mono text-xs font-semibold text-slate-700 hover:text-blue-600">{a.role}</span>
                      {roleDescBy[a.role] && <span className="block text-[11px] text-slate-400 truncate">{roleDescBy[a.role]}</span>}
                    </span>
                    <span className="shrink-0 flex items-center gap-1">
                      <AssignBadge toDate={a.toDate} />
                      <span className="text-slate-300 text-xs">›</span>
                    </span>
                  </button>
                ))}
                {!rolesList.length && <p className="py-6 text-center text-slate-400 text-sm">{t('sapRole.noRoles')}</p>}
              </div>
            </div>
          </div>
        );
      })()}
      {/* ── Modal: detail object utk user (field/value + role terkait) ── */}
      {objModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setObjModal(null)}>
          <div className="w-full max-w-lg max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <KeyRound className="text-blue-500 shrink-0" style={{ width: 16, height: 16 }} />
                  <span className="font-mono font-bold text-slate-800">{objModal.object}</span>
                </div>
                {(objModalData?.description || objDesc[objModal.object]) && (
                  <p className="text-xs text-slate-500 mt-0.5">{objModalData?.description || objDesc[objModal.object]}</p>
                )}
                <p className="text-[11px] text-slate-400 mt-0.5">{t('sapRole.user')} <span className="font-semibold text-slate-600">{objModal.username}</span></p>
              </div>
              <button onClick={() => setObjModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {objModalBusy && <p className="text-sm text-slate-400 py-4 text-center">{t('sapRole.loading')}</p>}
              {objModalData?.error && <p className="text-sm text-rose-500 py-2">{objModalData.error}</p>}
              {objModalData && !objModalBusy && !objModalData.error && (
                <>
                  <p className="text-xs font-semibold text-slate-500 mb-2">
                    {t('sapRole.userSRolesContainingObject')} ({objModalData.roles.length})
                  </p>
                  <div className="space-y-2">
                    {objModalData.roles.map(r => (
                      <div key={r.role} className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
                        <div className="flex items-baseline gap-2 mb-1">
                          <button onClick={() => { setObjModal(null); openDetail(r.role); }}
                            className="font-mono text-xs font-bold text-slate-700 hover:text-blue-600">{r.role}</button>
                          {r.description && <span className="text-[11px] text-slate-400 truncate">{r.description}</span>}
                        </div>
                        {r.fields.length ? (
                          <div className="flex flex-wrap gap-1">
                            {r.fields.map((f, i) => (
                              <span key={`${f.field}-${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-white border border-slate-200">
                                <span className="text-slate-400">{f.field}</span>
                                <span className="text-slate-700">{f.value || '∅'}</span>
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-[10px] text-slate-400">{t('sapRole.noFields')}</span>}
                      </div>
                    ))}
                    {!objModalData.roles.length && <p className="text-sm text-slate-400 py-2">{t('sapRole.noRelatedRoles')}</p>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Drawer detail role (desktop: side panel; mobile: full-width) ── */}
      {detailFor && (() => {
        // Tutup detail role saja. Kalau dibuka dari drawer user, drawer user (yang
        // tetap terbuka di belakang) akan tampil kembali; kalau tidak, kembali ke list.
        const closeDetail = () => { setDetailFor(null); setDetailBackUser(null); setDetailMax(false); };
        return (
          <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={closeDetail}>
            <div className={`w-full ${detailMax ? 'max-w-5xl' : 'max-w-md'} h-full bg-white shadow-2xl overflow-y-auto transition-[max-width] duration-200`} onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-slate-800 truncate">{detailFor}</h3>
                  {detailRole?.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{detailRole.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canWrite && (
                    <button onClick={() => { setFilePreset(null); setFileEditorOpen(true); }}
                      title={t('sapRole.openValueEditorDownloadSap')}
                      className="px-2.5 py-1.5 rounded-xl text-[11px] font-semibold border border-blue-300 text-blue-600 hover:bg-blue-50">
                      {t('sapRole.valueEditorSap')}
                    </button>
                  )}
                  <button onClick={() => setDetailMax(v => !v)}
                    title={detailMax ? (t('sapRole.restore')) : (t('sapRole.maximize'))}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                    {detailMax ? <Minimize2 style={{ width: 18, height: 18 }} /> : <Maximize2 style={{ width: 18, height: 18 }} />}
                  </button>
                  <button onClick={closeDetail} title={detailBackUser ? `${t('sapRole.back')} ${detailBackUser}` : ''}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
                </div>
              </div>
              <div className="p-5">
                <RoleDetailBody id={id} role={detailRole} detail={detail} busy={detailBusy} tab={detailTab} setTab={setDetailTab} openDetail={openDetail} tcodeDesc={tcodeDesc} objDesc={objDesc} canWrite={canWrite} onEditAuth={(p) => openAuthEdit(detailFor, p)} menu={roleMenu} menuBusy={roleMenuBusy} onRoleUser={roleUserChange} onRoleTcode={roleTcodeChange} writeBusy={roleWriteBusy} writeMsg={roleWriteMsg} onClearWriteMsg={() => setRoleWriteMsg(null)} onCopyRole={promptCopyRole} onDeleteRole={promptDeleteRole} />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Isi drawer detail role — dipakai desktop (side panel) & mobile (DetailSheet).
function RoleDetailBody({ id, role, detail, busy, tab, setTab, openDetail, tcodeDesc = {}, objDesc = {}, canWrite = false, onEditAuth, menu = null, menuBusy = false, onRoleUser, onRoleTcode, writeBusy = false, writeMsg = null, onClearWriteMsg, onCopyRole, onDeleteRole }) {
  const {
    t
  } = useLang();

  const [q, setQ] = useState('');
  const [newUser, setNewUser] = useState('');
  const [newTcode, setNewTcode] = useState('');
  const [authView, setAuthView] = useState('class');   // tab Auth: 'class' (pohon per kelas) | 'flat'
  useEffect(() => { setQ(''); }, [tab]);   // reset pencarian saat ganti tab
  if (busy) return <p className="text-sm text-slate-400 py-6 text-center">{t('sapRole.loadingDetail')}</p>;
  if (!detail) return null;
  if (detail.error) return <p className="text-sm text-rose-500 py-4">{detail.error}</p>;

  const tabs = [
    { key: 'users',  label: `User (${detail.users.length})`,    Icon: Users },
    { key: 'tcodes', label: `Tcode (${detail.tcodes.length})`,  Icon: TerminalSquare },
    { key: 'auth',   label: `Auth (${detail.auth ? detail.auth.length : 0})`, Icon: KeyRound },
    ...(detail.children.length ? [{ key: 'children', label: `Child (${detail.children.length})`, Icon: Layers }] : []),
    { key: 'menu', label: `Menu${menu && menu.role === (role?.name || '') && typeof menu.count === 'number' ? ` (${menu.count})` : ''}`, Icon: GitBranch },
  ];
  const qq = q.trim().toUpperCase();
  const fUsers = qq ? detail.users.filter(u => (u.username || '').toUpperCase().includes(qq)) : detail.users;
  const fTcodes = qq ? detail.tcodes.filter(t => t.toUpperCase().includes(qq) || (tcodeDesc[t] || '').toUpperCase().includes(qq)) : detail.tcodes;
  const fAuth = qq
    ? (detail.auth || []).filter(a => (a.object || '').toUpperCase().includes(qq) || (a.auth || '').toUpperCase().includes(qq) || (objDesc[a.object] || '').toUpperCase().includes(qq) || (a.fields || []).some(f => (f.field || '').toUpperCase().includes(qq) || (f.value || '').toUpperCase().includes(qq)))
    : (detail.auth || []);
  const fChildren = qq ? detail.children.filter(c => c.toUpperCase().includes(qq)) : detail.children;
  // Filter pohon menu: simpan node bila cocok atau punya keturunan yang cocok.
  const filterMenu = (list) => (list || []).map(n => {
    const kids = filterMenu(n.children);
    const self = !qq || (n.text || '').toUpperCase().includes(qq) || (n.tcode || '').toUpperCase().includes(qq);
    return (self || kids.length) ? { ...n, children: kids } : null;
  }).filter(Boolean);
  const menuTree = menu && menu.tree ? (qq ? filterMenu(menu.tree) : menu.tree) : [];
  const MenuNodes = ({ list, depth = 0 }) => (
    <>
      {list.map(n => (
        <div key={n.id}>
          <div className="flex items-start gap-2 py-1" style={{ paddingLeft: depth * 16 }}>
            {n.isFolder
              ? <Layers style={{ width: 13, height: 13, marginTop: 2 }} className="text-amber-500 shrink-0" />
              : <TerminalSquare style={{ width: 13, height: 13, marginTop: 2 }} className="text-blue-500 shrink-0" />}
            <span className="text-xs">
              {n.tcode && <span className="font-mono font-semibold text-slate-700">{n.tcode}</span>}
              {n.tcode && n.text ? <span className="text-slate-400"> — </span> : null}
              <span className={n.isFolder ? 'font-semibold text-slate-700' : 'text-slate-600'}>{n.text || (n.isFolder ? (id ? '(folder)' : '(folder)') : '')}</span>
            </span>
            {canWrite && onRoleTcode && n.tcode && !n.isFolder && (
              <button disabled={writeBusy} onClick={() => onRoleTcode(n.tcode, 'remove')} title="Remove from menu and S_TCODE"
                className="ml-auto text-[10px] px-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40">Remove</button>
            )}
          </div>
          {n.children && n.children.length > 0 && <MenuNodes list={n.children} depth={depth + 1} />}
        </div>
      ))}
    </>
  );
  return (
    <div>
      {role && (
        <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <span className="text-slate-400">{t('sapRole.type')}</span>
            <p className="font-semibold text-slate-700">{role.isComposite ? 'Composite' : role.parentAgr ? `Derived (${role.parentAgr})` : 'Single'}</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <span className="text-slate-400">{t('sapRole.created')}</span>
            <p className="font-semibold text-slate-700">{role.createdBy || '—'}{role.createdOn ? ` · ${role.createdOn}` : ''}</p>
          </div>
        </div>
      )}
      {canWrite && role && (onCopyRole || onDeleteRole) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {onCopyRole && <button disabled={writeBusy} onClick={() => onCopyRole(role.name)}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-40">Copy role…</button>}
          {onDeleteRole && <button disabled={writeBusy || (detail.users || []).length > 0} onClick={() => onDeleteRole(role.name)}
            title={(detail.users || []).length > 0 ? 'Remove all users from this role first' : 'Delete this role'}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-40">Delete role…</button>}
        </div>
      )}
      <div className="flex gap-1.5 mb-3">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border
              ${tab === t.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
            <t.Icon style={{ width: 13, height: 13 }} />{t.label}
          </button>
        ))}
      </div>
      {writeMsg && (
        <div className={`mb-3 flex items-start justify-between gap-2 rounded-xl border px-3 py-2 text-xs ${writeMsg.type === 'error' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          <span className="break-words">{writeMsg.text}</span>
          {onClearWriteMsg && <button onClick={onClearWriteMsg} className="shrink-0 opacity-60 hover:opacity-100">×</button>}
        </div>
      )}
      <div className="mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t('sapRole.searchTab')}
          className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-300" />
      </div>
      {tab === 'users' && canWrite && onRoleUser && (
        <div className="mb-3 flex gap-2">
          <input value={newUser} onChange={(e) => setNewUser(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter' && newUser.trim() && !writeBusy) { onRoleUser(newUser.trim(), 'add'); setNewUser(''); } }}
            placeholder="SAP username to assign this role"
            className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-mono uppercase outline-none focus:border-emerald-300" />
          <button disabled={!newUser.trim() || writeBusy} onClick={() => { onRoleUser(newUser.trim(), 'add'); setNewUser(''); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${newUser.trim() && !writeBusy ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-slate-200 text-slate-300'}`}>
            {writeBusy ? 'Working…' : 'Assign user'}
          </button>
        </div>
      )}
      {tab === 'users' && (
        <ul className="divide-y divide-slate-100 text-sm">
          {fUsers.map(u => {
            const unlimited = !u.toDate || u.toDate.startsWith('9999');
            const dd = unlimited ? null : daysUntil(u.toDate);
            const expired = dd !== null && dd < 0;
            const soon = dd !== null && dd >= 0 && dd <= 30;
            return (
              <li key={u.username} className="py-2 flex items-center justify-between gap-2">
                <span className="font-medium text-slate-700">{u.username}</span>
                <span className="flex items-center gap-2">
                  <span className={`text-xs ${expired ? 'text-rose-500 font-semibold' : soon ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>
                    {unlimited ? (t('sapRole.unlimited')) : `${u.fromDate || '?'} → ${u.toDate}${expired ? (id ? ' (expired)' : ' (expired)') : soon ? ` (≤${dd}${t('sapRole.d')})` : ''}`}
                  </span>
                  {canWrite && onRoleUser && (
                    <button disabled={writeBusy} onClick={() => onRoleUser(u.username, 'remove')} title="Remove this role from the user"
                      className="px-1.5 py-0.5 rounded text-[10px] font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40">Remove</button>
                  )}
                </span>
              </li>
            );
          })}
          {!fUsers.length && <li className="py-4 text-center text-slate-400 text-sm">{qq ? (t('sapRole.noMatch')) : (t('sapRole.noUsersAssigned'))}</li>}
        </ul>
      )}
      {(tab === 'tcodes' || tab === 'menu') && canWrite && onRoleTcode && role.isComposite && (
        <p className="mb-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-800">
          This is a composite role — it has no authorizations of its own. To add a tcode, open one of its child roles (Child tab) and add it there.
        </p>
      )}
      {(tab === 'tcodes' || tab === 'menu') && canWrite && onRoleTcode && !role.isComposite && (
        <div className="mb-3">
          <div className="flex gap-2">
            <input value={newTcode} onChange={(e) => setNewTcode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter' && newTcode.trim() && !writeBusy) { onRoleTcode(newTcode.trim(), 'add'); setNewTcode(''); } }}
              placeholder="Transaction code to add to menu + S_TCODE"
              className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-mono uppercase outline-none focus:border-emerald-300" />
            <button disabled={!newTcode.trim() || writeBusy} onClick={() => { onRoleTcode(newTcode.trim(), 'add'); setNewTcode(''); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${newTcode.trim() && !writeBusy ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-slate-200 text-slate-300'}`}>
              {writeBusy ? 'Working…' : 'Add tcode'}
            </button>
          </div>
          <p className="mt-1 text-[10px] text-slate-400">Adds the tcode to the role menu and S_TCODE, then generates. SU24 proposals are NOT merged — add other required objects via Edit Authorizations.</p>
        </div>
      )}
      {tab === 'tcodes' && (
        <div className="flex flex-wrap gap-1.5">
          {fTcodes.map(t => (
            <span key={t} title={tcodeDesc[t] || ''} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-mono bg-slate-50 text-slate-600 border border-slate-200">
              {t}
              {canWrite && onRoleTcode && (
                <button disabled={writeBusy} onClick={() => onRoleTcode(t, 'remove')} title="Remove from menu and S_TCODE"
                  className="text-slate-400 hover:text-rose-600 disabled:opacity-40">×</button>
              )}
            </span>
          ))}
          {!fTcodes.length && <p className="text-sm text-slate-400 py-2">{qq ? (t('sapRole.noMatch')) : (t('sapRole.noTcodesPossiblyCompositeRole'))}</p>}
        </div>
      )}
      {tab === 'auth' && (
        <div>
          {canWrite && onEditAuth && (
            <button onClick={() => onEditAuth()}
              className="mb-3 px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700">
              {t('sapRole.editAuthorizationsWriteSap')}
            </button>
          )}
          <div className="mb-3 inline-flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-semibold">
            <button onClick={() => setAuthView('class')} className={`px-2.5 py-1 ${authView === 'class' ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{t('sapRole.class')}</button>
            <button onClick={() => setAuthView('flat')} className={`px-2.5 py-1 ${authView === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>Flat</button>
          </div>
          {detail.authError && (
            <p className="text-xs text-amber-600 mb-2">{t('sapRole.failedReadAuthObjects')}{detail.authError}</p>
          )}
          {fAuth.length ? (() => {
            const AuthLi = ({ a, i }) => (
              <li key={`${a.object}|${a.auth}|${i}`} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  {canWrite && onEditAuth ? (
                    <button onClick={() => onEditAuth({ objekt: a.object, auth: a.auth })}
                      title={`${a.objText || objDesc[a.object] || ''}\nClick to edit this object (insert a value / delete object)`}
                      className="font-mono text-xs font-bold text-slate-700 hover:text-rose-600 hover:underline">{a.object}</button>
                  ) : (
                    <span className="font-mono text-xs font-bold text-slate-700" title={a.objText || objDesc[a.object] || ''}>{a.object}</span>
                  )}
                  {(a.objText || objDesc[a.object]) && <span className="text-[10px] text-slate-500 truncate">{a.objText || objDesc[a.object]}</span>}
                  {a.auth && <span className="font-mono text-[10px] text-slate-400 shrink-0">{a.auth}</span>}
                </div>
                {a.fields.length ? (
                  <div className="flex flex-wrap gap-1">
                    {a.fields.map((f, j) => {
                      const inner = (<>
                        <span className="text-slate-400">{f.field}</span>
                        <span className="text-slate-700">{f.value || '∅'}</span>
                      </>);
                      if (!(canWrite && onEditAuth)) {
                        return <span key={`${f.field}-${j}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-white border border-slate-200 text-slate-600">{inner}</span>;
                      }
                      // Value dari backend: "LOW" atau "LOW – HIGH" → pecah lagi untuk prefill editor (gaya PFCG).
                      const parts = String(f.value || '').split(' – ');
                      const low = parts[0] || '', high = parts.length > 1 ? parts[1] : '';
                      return (
                        <button key={`${f.field}-${j}`} onClick={() => onEditAuth({ objekt: a.object, auth: a.auth, field: f.field, low, high })}
                          title={f.value ? 'Click to edit this value (replace / delete)' : 'Field is empty — click to insert a value'}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-white border border-slate-200 text-slate-600 hover:border-rose-400 hover:bg-rose-50 cursor-pointer">{inner}</button>
                      );
                    })}
                  </div>
                ) : <span className="text-[11px] text-slate-400">{t('sapRole.noFields')}</span>}
              </li>
            );
            if (authView === 'flat') {
              return <ul className="space-y-2">{fAuth.map((a, i) => <AuthLi key={i} a={a} i={i} />)}</ul>;
            }
            // By Class: kelompokkan per className (fallback oclass / "(tanpa kelas)").
            const groups = {};
            fAuth.forEach((a, i) => {
              const key = a.className || a.oclass || (t('sapRole.noClass'));
              (groups[key] = groups[key] || []).push({ a, i });
            });
            const order = Object.keys(groups).sort((x, y) => x.localeCompare(y));
            return (
              <div className="space-y-3">
                {order.map(cls => (
                  <div key={cls}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Layers style={{ width: 13, height: 13 }} className="text-amber-500 shrink-0" />
                      <span className="text-xs font-semibold text-slate-700">{cls}</span>
                      <span className="text-[10px] text-slate-400">({groups[cls].length})</span>
                    </div>
                    <ul className="space-y-2 pl-4">{groups[cls].map(({ a, i }) => <AuthLi key={i} a={a} i={i} />)}</ul>
                  </div>
                ))}
              </div>
            );
          })() : !detail.authError && (
            <p className="text-sm text-slate-400 py-2">{qq ? (t('sapRole.noMatch')) : (t('sapRole.noAuthorizationObjects'))}</p>
          )}
        </div>
      )}
      {tab === 'children' && (
        <div className="flex flex-wrap gap-1.5">
          {fChildren.map(c => (
            <button key={c} onClick={() => openDetail(c)}
              className="inline-flex px-2 py-1 rounded-lg text-[11px] font-medium bg-violet-50 text-violet-600 border border-violet-200 hover:border-violet-400">
              {c}
            </button>
          ))}
          {!fChildren.length && <p className="text-sm text-slate-400 py-2">{qq ? (t('sapRole.noMatch')) : (t('sapRole.noChildRoles'))}</p>}
        </div>
      )}
      {tab === 'menu' && (
        <div>
          {menuBusy && <p className="text-sm text-slate-400 py-4 text-center">{t('sapRole.loadingMenu')}</p>}
          {!menuBusy && menu && menu.error && <p className="text-xs text-rose-500 py-2">{menu.error}</p>}
          {!menuBusy && menu && !menu.error && (
            menuTree.length
              ? <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-2">{React.createElement(MenuNodes, { list: menuTree })}</div>
              : <p className="text-sm text-slate-400 py-2">{qq ? (t('sapRole.noMatch')) : (t('sapRole.roleMenuEmpty'))}</p>
          )}
        </div>
      )}
    </div>
  );
}
